import React, { useState, useEffect, useMemo, Suspense, lazy } from 'react';
import { auth, signInWithGoogle } from '../firebase';
import { signOut } from 'firebase/auth';
import { useAuth } from '../components/AuthContext';

// 🚀 LAZY IMPORTS FOR PERFORMANCE
const TriageModal = lazy(() => import('./TriageModal'));
const SupervisionTriageModal = lazy(() => import('./SupervisionTriageModal'));
const JdrRadarWidget = lazy(() => import('./JdrRadarWidget'));

const LineChart = lazy(() => import('recharts').then(m => ({ default: m.LineChart })));
const Line = lazy(() => import('recharts').then(m => ({ default: m.Line })));
const XAxis = lazy(() => import('recharts').then(m => ({ default: m.XAxis })));
const YAxis = lazy(() => import('recharts').then(m => ({ default: m.YAxis })));
const CartesianGrid = lazy(() => import('recharts').then(m => ({ default: m.CartesianGrid })));
const Tooltip = lazy(() => import('recharts').then(m => ({ default: m.Tooltip })));
const ResponsiveContainer = lazy(() => import('recharts').then(m => ({ default: m.ResponsiveContainer })));
const Legend = lazy(() => import('recharts').then(m => ({ default: m.Legend })));

import { 
    SignInIcon, 
    SignOutIcon, 
    ChartLineUpIcon, 
    WindowCloseIcon, 
    ProfileGearIcon,
    RefreshIcon,
    ArrowCircleLeftIcon,
    ShareIcon 
} from '../components/Icons';

// ==========================================
// 🚦 TRAFFIC LIGHT STATE VISUALIZER COMPONENT
// ==========================================
const TrafficLightVisualizer = ({ scores }) => {
    if (!scores) return null;

    // 🛡️ Normalizes Objects or Arrays
    let items = [];
    if (Array.isArray(scores)) {
        items = scores;
    } else if (typeof scores === 'object') {
        items = Object.values(scores);
    }

    if (items.length === 0) return null;

    const colors = {
        red: '#ef4444',
        amber: '#f59e0b',
        green: '#10b981'
    };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '15px', padding: '10px 0' }}>
            {items.map((item, idx) => {
                const label = item.label || item.name || `Light ${idx + 1}`;
                
                // 🛡️ Robust property extraction (catches state, selected, val, color, value, level)
                const rawVal = item.state ?? item.selected ?? item.status ?? item.color ?? item.val ?? item.value ?? item.level ?? item;
                const strVal = String(rawVal).toLowerCase().trim();

                // 🚥 Multi-format matching (supports 'orange', 'amber', 'yellow', '1', etc.)
                let currentState = 'green';
                if (strVal.includes('red') || strVal === '0') {
                    currentState = 'red';
                } else if (strVal.includes('amber') || strVal.includes('orange') || strVal.includes('yellow') || strVal === '1') {
                    currentState = 'amber';
                } else if (strVal.includes('green') || strVal === '2') {
                    currentState = 'green';
                }

                return (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--trait-bg)', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                        <div style={{ background: '#1e293b', padding: '8px 10px', borderRadius: '16px', display: 'flex', flexDirection: 'column', gap: '6px', border: '1px solid var(--border-subtle)', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
                            {['red', 'amber', 'green'].map(lightColor => {
                                const isActive = currentState === lightColor;
                                return (
                                    <div 
                                        key={lightColor} 
                                        style={{ 
                                            width: '18px', 
                                            height: '18px', 
                                            borderRadius: '50%', 
                                            background: isActive ? colors[lightColor] : 'rgba(255,255,255,0.1)', 
                                            boxShadow: isActive ? `0 0 10px ${colors[lightColor]}` : 'none',
                                            transition: 'all 0.3s ease'
                                        }} 
                                    />
                                );
                            })}
                        </div>
                        <span style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', color: 'var(--text-main)', marginTop: '10px', textAlign: 'center', wordBreak: 'break-word' }}>
                            {label}
                        </span>
                        
                    </div>
                );
            })}
        </div>
    );
};

// ==========================================
// 🎯 TARGET PROXIMITY VISUALIZER COMPONENT (EXACT SCALE REPLICA)
// ==========================================
const TargetVisualizer = ({ scores }) => {
    if (!scores) return null;
    
    // 🛡️ Normalizes Objects (e.g. {"0": {...}, "1": {...}}) or Arrays
    let items = [];
    if (Array.isArray(scores)) {
        items = scores;
    } else if (typeof scores === 'object') {
        items = Object.values(scores);
    }

    if (items.length === 0) return null;

    // Exact curved path used on the blue ring in TargetApp
    const CURVE_PATH = "M 37,100 A 55,55 0 0,0 163,100";

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '15px', padding: '10px 0' }}>
            {items.map((item, idx) => {
                const label = item.label || item.name || `Target ${idx + 1}`;
                
                // Fallback position calculation if x/y coordinates are missing in older records
                let posX = item.x;
                let posY = item.y;
                if (posX === undefined && typeof item.distance === 'number') {
                    const angle = (idx * (360 / Math.max(items.length, 1)) - 45) * (Math.PI / 180);
                    posX = 100 + item.distance * Math.cos(angle);
                    posY = 100 + item.distance * Math.sin(angle);
                }

                return (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--trait-bg)', padding: '14px 10px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                        <svg viewBox="0 0 200 200" style={{ width: '110px', height: '110px', overflow: 'visible', filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.3))' }}>
                            <defs>
                                <path id={`dash-curve-${idx}`} d={CURVE_PATH} fill="transparent" />
                            </defs>

                            {/* 1. Outer White / Gray Edge (83.5% / 82.3%) */}
                            <circle cx="100" cy="100" r="83.5" fill="#cccccc" />
                            <circle cx="100" cy="100" r="82.3" fill="#FFFFFF" />

                            {/* 2. Black Band (66.4%) */}
                            <circle cx="100" cy="100" r="66.4" fill="#111111" />

                            {/* 3. Blue Band (49.8%) */}
                            <circle cx="100" cy="100" r="49.8" fill="#0074D9" />

                            {/* 4. Red Band (33.1%) */}
                            <circle cx="100" cy="100" r="33.1" fill="#FF4136" />

                            {/* 5. Gold Bullseye Center (16.4%) */}
                            <circle cx="100" cy="100" r="16.4" fill="#FFD700" />

                            {/* 6. Center Black Crosshair (+) */}
                            <line x1="91.5" y1="100" x2="108.5" y2="100" stroke="#000000" strokeWidth="2.5" strokeLinecap="round" />
                            <line x1="100" y1="91.5" x2="100" y2="108.5" stroke="#000000" strokeWidth="2.5" strokeLinecap="round" />

                            {/* 7. Curved Label Text on Blue Band */}
                            {label && (
                                <text style={{ fill: '#ffffff', fontFamily: 'Inter, sans-serif', fontWeight: '800', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '2px' }}>
                                    <textPath href={`#dash-curve-${idx}`} startOffset="50%" textAnchor="middle">
                                        {label}
                                    </textPath>
                                </text>
                            )}

                            {/* 8. Plotted Marker 'X' (Identical white cross with black borders) */}
                            {posX !== undefined && posY !== undefined && (
                                <g transform={`translate(${posX}, ${posY})`}>
                                    <rect x="-12" y="-3" width="24" height="6" rx="3" fill="#ffffff" stroke="#000000" strokeWidth="2" transform="rotate(45)" />
                                    <rect x="-12" y="-3" width="24" height="6" rx="3" fill="#ffffff" stroke="#000000" strokeWidth="2" transform="rotate(-45)" />
                                </g>
                            )}
                        </svg>

                        <span style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', color: 'var(--text-main)', marginTop: '12px', textAlign: 'center', wordBreak: 'break-word', letterSpacing: '1px' }}>
                            {label}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

// ==========================================
// 🧭 COMPASS POINTS VISUALIZER COMPONENT (VERTICALLY STACKED / ESSAY-READY)
// ==========================================
const CompassVisualizer = ({ scores }) => {
    if (!scores) return null;

    const data = typeof scores === 'object' ? scores : {};

    // Strictly ordered: 1. Excited, 2. Need to know, 3. Worrisome, 4. Stance/Suggestions
    const categories = [
        {
            key: 'excited',
            title: 'E — Excited',
            subtitle: 'What excites you about this idea or proposition? What’s the upside?',
            content: data.excited ?? data.E ?? data.Excited ?? '',
            color: '#10b981' // Emerald Accent
        },
        {
            key: 'needToKnow',
            title: 'N — Need to Know',
            subtitle: 'What else do you need to know or find out about this idea or proposition?',
            content: data.needToKnow ?? data.N ?? data.NeedToKnow ?? data['need to know'] ?? '',
            color: '#3b82f6' // Sapphire Blue
        },
        {
            key: 'worrisome',
            title: 'W — Worrisome',
            subtitle: 'What do you find worrisome about this idea or proposition? What’s the downside?',
            content: data.worrisome ?? data.W ?? data.Worrisome ?? '',
            color: '#ef4444' // Terracotta Red
        },
        {
            key: 'stance',
            title: 'S — Stance / Suggestion',
            subtitle: 'What is your current stance or opinion? How might you move forward?',
            content: data.stance ?? data.S ?? data.Stance ?? data.suggestion ?? '',
            color: '#f59e0b' // Amber
        }
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '10px 0', width: '100%', boxSizing: 'border-box' }}>
            {categories.map((cat) => {
                const hasText = typeof cat.content === 'string' && cat.content.trim().length > 0;
                return (
                    <div 
                        key={cat.key} 
                        style={{ 
                            background: 'var(--trait-bg)', 
                            border: '1px solid var(--border-subtle)', 
                            borderLeft: `4px solid ${cat.color}`,
                            borderRadius: '12px', 
                            padding: '18px 20px', 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '10px',
                            width: '100%',
                            boxSizing: 'border-box'
                        }}
                    >
                        <div style={{ borderBottom: '1px solid var(--border-subtle)', paddingBottom: '8px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', color: cat.color }}>
                                {cat.title}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px', lineHeight: '1.4' }}>
                                {cat.subtitle}
                            </div>
                        </div>
                        <div style={{ fontSize: '14px', lineHeight: '1.6', color: 'var(--text-main)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', paddingTop: '2px' }}>
                            {hasText ? cat.content : <em style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No response recorded.</em>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ==========================================
// 🔗 CONNECT, EXTEND, CHALLENGE (CEC) VISUALIZER COMPONENT
// ==========================================
const CecVisualizer = ({ scores }) => {
    if (!scores) return null;

    const data = typeof scores === 'object' ? scores : {};

    const nodesDef = [
        { label: "Connect", key: "Connect", q: ["How do the ideas and information presented CONNECT to what you already know?"] },
        { label: "Extend", key: "Extend", q: ["What new ideas did you get that EXTENDED or pushed your thinking in new directions?"] },
        { label: "Challenge", key: "Challenge", q: ["What is still CHALLENGING or confusing for you to get your mind around? What questions, wonderings or puzzles do you now have?"] }
    ];

    const getAnswers = (nodeKey) => {
        const raw = data[nodeKey] ?? data[nodeKey.toLowerCase()] ?? [];
        if (Array.isArray(raw)) return raw.map(v => String(v ?? '').trim());
        if (typeof raw === 'string') return [raw.trim()];
        return [];
    };

    const isNodeActive = (nodeKey) => getAnswers(nodeKey).some(a => a.length > 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', boxSizing: 'border-box' }}>
            {/* 1. EXACT SCALE GRAPHICAL REPLICA */}
            <div style={{ position: 'relative', width: '220px', height: '220px', margin: '0 auto', background: 'var(--trait-bg)', borderRadius: '16px', border: '1px solid var(--border-subtle)', padding: '10px' }}>
                <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
                    <line x1="25%" y1="25%" x2="50%" y2="75%" stroke={isNodeActive('Connect') && isNodeActive('Extend') ? "#31b1c6" : "var(--border-subtle)"} strokeWidth="2.5" strokeDasharray={isNodeActive('Connect') && isNodeActive('Extend') ? "none" : "4,4"} />
                    <line x1="50%" y1="75%" x2="75%" y2="25%" stroke={isNodeActive('Extend') && isNodeActive('Challenge') ? "#31b1c6" : "var(--border-subtle)"} strokeWidth="2.5" strokeDasharray={isNodeActive('Extend') && isNodeActive('Challenge') ? "none" : "4,4"} />
                    <line x1="25%" y1="25%" x2="75%" y2="25%" stroke={isNodeActive('Connect') && isNodeActive('Challenge') ? "#31b1c6" : "var(--border-subtle)"} strokeWidth="2.5" strokeDasharray={isNodeActive('Connect') && isNodeActive('Challenge') ? "none" : "4,4"} />
                </svg>

                <div style={{ position: 'absolute', left: '25%', top: '25%', transform: 'translate(-50%, -50%)', width: '60px', height: '60px', borderRadius: '50%', background: 'var(--card-bg)', border: isNodeActive('Connect') ? '3px solid #31b1c6' : '2px solid var(--border-subtle)', color: 'var(--text-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', boxShadow: '0 4px 10px var(--card-shadow)' }}>Connect</div>
                <div style={{ position: 'absolute', left: '50%', top: '75%', transform: 'translate(-50%, -50%)', width: '60px', height: '60px', borderRadius: '50%', background: 'var(--card-bg)', border: isNodeActive('Extend') ? '3px solid #31b1c6' : '2px solid var(--border-subtle)', color: 'var(--text-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', boxShadow: '0 4px 10px var(--card-shadow)' }}>Extend</div>
                <div style={{ position: 'absolute', left: '75%', top: '25%', transform: 'translate(-50%, -50%)', width: '60px', height: '60px', borderRadius: '50%', background: 'var(--card-bg)', border: isNodeActive('Challenge') ? '3px solid #31b1c6' : '2px solid var(--border-subtle)', color: 'var(--text-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', boxShadow: '0 4px 10px var(--card-shadow)' }}>Challenge</div>
            </div>

            {/* 2. VERTICAL STACK OF NON-EMPTY RESPONSES ONLY */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%' }}>
                {nodesDef.map(node => {
                    const ansList = getAnswers(node.key);
                    const filledEntries = node.q.map((qText, i) => ({ question: qText, answer: ansList[i] || '' })).filter(e => e.answer.length > 0);

                    if (filledEntries.length === 0) return null; // 🛡️ Hide empty fields completely

                    return (
                        <div key={node.key} style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderLeft: '4px solid #31b1c6', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', color: '#31b1c6', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '6px' }}>
                                {node.label}
                            </div>
                            {filledEntries.map((entry, idx) => (
                                <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-muted)' }}>{entry.question}</div>
                                    <div style={{ fontSize: '13.5px', lineHeight: '1.5', color: 'var(--text-main)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{entry.answer}</div>
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ==========================================
// ⭕ THE 3YS (PURPOSE ALIGNMENT) VISUALIZER COMPONENT
// ==========================================
const ThreeYsVisualizer = ({ scores }) => {
    if (!scores) return null;

    const data = typeof scores === 'object' ? scores : {};

    const ysDef = [
        { label: "Personal", key: "Personal", color: '#2c3e50', q: ["Why does this matter to me?"] },
        { label: "Local", key: "Local", color: '#3498db', q: ["Why does it matter to my family, community, or immediate circle?"] },
        { label: "Global", key: "Global", color: '#31b1c6', q: ["Why does it matter to the world?"] }
    ];

    const getAnswers = (key) => {
        const raw = data[key] ?? data[key.toLowerCase()] ?? [];
        if (Array.isArray(raw)) return raw.map(v => String(v ?? '').trim());
        if (typeof raw === 'string') return [raw.trim()];
        return [];
    };

    const isNodeActive = (key) => getAnswers(key).some(a => a.length > 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', boxSizing: 'border-box' }}>
            {/* 1. EXACT SCALE GRAPHICAL REPLICA */}
            <div style={{ width: '200px', height: '200px', margin: '0 auto', background: 'var(--trait-bg)', borderRadius: '16px', border: '1px solid var(--border-subtle)', padding: '10px' }}>
                <svg viewBox="0 0 400 400" style={{ width: '100%', height: '100%' }}>
                    <circle cx="200" cy="200" r="180" fill={isNodeActive('Global') ? '#31b1c6' : 'rgba(128,128,128,0.2)'} stroke="var(--bg-main)" strokeWidth="4" />
                    <text x="200" y="45" style={{ fontFamily: 'inherit', fontSize: '18px', fontWeight: '800', textAnchor: 'middle', fill: isNodeActive('Global') ? '#fff' : 'var(--text-muted)', letterSpacing: '2px' }}>GLOBAL</text>
                    
                    <circle cx="200" cy="200" r="120" fill={isNodeActive('Local') ? '#3498db' : 'rgba(128,128,128,0.4)'} stroke="var(--bg-main)" strokeWidth="4" />
                    <text x="200" y="105" style={{ fontFamily: 'inherit', fontSize: '18px', fontWeight: '800', textAnchor: 'middle', fill: isNodeActive('Local') ? '#fff' : 'var(--text-main)', letterSpacing: '2px' }}>LOCAL</text>
                    
                    <circle cx="200" cy="200" r="60" fill={isNodeActive('Personal') ? '#2c3e50' : 'rgba(128,128,128,0.6)'} stroke="var(--bg-main)" strokeWidth="4" />
                    <text x="200" y="205" style={{ fontFamily: 'inherit', fontSize: '16px', fontWeight: '800', textAnchor: 'middle', fill: isNodeActive('Personal') ? '#fff' : 'var(--text-main)', letterSpacing: '2px' }}>PERSONAL</text>
                </svg>
            </div>

            {/* 2. VERTICAL STACK OF NON-EMPTY RESPONSES ONLY */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%' }}>
                {ysDef.map(node => {
                    const ansList = getAnswers(node.key);
                    const filledEntries = node.q.map((qText, i) => ({ question: qText, answer: ansList[i] || '' })).filter(e => e.answer.length > 0);

                    if (filledEntries.length === 0) return null; // 🛡️ Hide empty fields completely

                    return (
                        <div key={node.key} style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderLeft: `4px solid ${node.color}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', color: node.color, borderBottom: '1px solid var(--border-subtle)', paddingBottom: '6px' }}>
                                {node.label} Alignment
                            </div>
                            {filledEntries.map((entry, idx) => (
                                <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-muted)' }}>{entry.question}</div>
                                    <div style={{ fontSize: '13.5px', lineHeight: '1.5', color: 'var(--text-main)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{entry.answer}</div>
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ==========================================
// 🕸️ COMPLEXITY ASSESSMENT VISUALIZER COMPONENT
// ==========================================
const ComplexityVisualizer = ({ scores }) => {
    if (!scores) return null;

    const data = typeof scores === 'object' ? scores : {};

    const compDef = [
        { label: "Parts & Interactions", key: "Parts & Interactions", q: ["What are all the parts?", "How do these parts interact? (casually and otherwise)?"] },
        { label: "Truth", key: "Truth", q: ["What are the undisputed facts and interpretations?", "What are the disputed facts and interpretations?", "What are the uncertainties?"] },
        { label: "Engagement", key: "Engagement", q: ["Who are you in relation to this?", "How are you connected?", "What values, beliefs, emotions, come into play for you?"] },
        { label: "Time", key: "Time", q: ["How does it change over time?", "What happened before; what might happen next?", "What causal factors are involved?"] },
        { label: "Perspective", key: "Perspective", q: ["What are all the different viewpoints, perspectives, lenses, stakeholders?"] }
    ];

    const getAnswers = (label) => {
        const raw = data[label] ?? data[label.toLowerCase()] ?? [];
        if (Array.isArray(raw)) return raw.map(v => String(v ?? '').trim());
        if (typeof raw === 'string') return [raw.trim()];
        return [];
    };

    const isNodeActive = (label) => getAnswers(label).some(a => a.length > 0);

    const getPos = (i) => {
        const angle = (i * (2 * Math.PI) / 5) - (Math.PI / 2);
        return { x: 50 + 38 * Math.cos(angle), y: 50 + 38 * Math.sin(angle) };
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', boxSizing: 'border-box' }}>
            {/* 1. EXACT SCALE GRAPHICAL REPLICA */}
            <div style={{ position: 'relative', width: '240px', height: '240px', margin: '0 auto', background: 'var(--trait-bg)', borderRadius: '16px', border: '1px solid var(--border-subtle)', padding: '10px' }}>
                <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
                    {compDef.map((_, i) => compDef.map((_, j) => i < j && isNodeActive(compDef[i].label) && isNodeActive(compDef[j].label) ? (
                        <line key={`${i}-${j}`} x1={`${getPos(i).x}%`} y1={`${getPos(i).y}%`} x2={`${getPos(j).x}%`} y2={`${getPos(j).y}%`} stroke="#31b1c6" strokeWidth="2.5" />
                    ) : null))}
                </svg>

                {compDef.map((node, i) => {
                    const active = isNodeActive(node.label);
                    const pos = getPos(i);
                    return (
                        <div 
                            key={node.key} 
                            style={{ 
                                position: 'absolute', 
                                left: `${pos.x}%`, 
                                top: `${pos.y}%`, 
                                transform: 'translate(-50%, -50%)', 
                                width: '58px', 
                                height: '58px', 
                                borderRadius: '50%', 
                                background: 'var(--card-bg)', 
                                border: active ? '3px solid #31b1c6' : '2px solid var(--border-subtle)', 
                                color: active ? 'var(--text-main)' : 'var(--text-muted)', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                textAlign: 'center', 
                                fontSize: '8.5px', 
                                fontWeight: '800', 
                                padding: '4px', 
                                boxSizing: 'border-box', 
                                boxShadow: '0 4px 10px var(--card-shadow)' 
                            }}
                        >
                            {node.label}
                        </div>
                    );
                })}
            </div>

            {/* 2. VERTICAL STACK OF NON-EMPTY RESPONSES ONLY */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%' }}>
                {compDef.map(node => {
                    const ansList = getAnswers(node.label);
                    const filledEntries = node.q.map((qText, i) => ({ question: qText, answer: ansList[i] || '' })).filter(e => e.answer.length > 0);

                    if (filledEntries.length === 0) return null; // 🛡️ Hide empty fields completely

                    return (
                        <div key={node.key} style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderLeft: '4px solid #31b1c6', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', color: '#31b1c6', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '6px' }}>
                                {node.label}
                            </div>
                            {filledEntries.map((entry, idx) => (
                                <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-muted)' }}>{entry.question}</div>
                                    <div style={{ fontSize: '13.5px', lineHeight: '1.5', color: 'var(--text-main)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{entry.answer}</div>
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ==========================================
// 🔺 STEP INSIDE PERSPECTIVE VISUALIZER COMPONENT
// ==========================================
const StepInsideVisualizer = ({ scores }) => {
    if (!scores) return null;

    const data = typeof scores === 'object' ? scores : {};

    const stepDef = [
        { label: "Perceive", key: "Perceive", color: "#2c3e50", q: ["What does this person see, hear, or notice in their environment?", "What details stand out to them that others might miss?"] },
        { label: "Know / Believe", key: "Know / Believe", color: "#2980b9", q: ["What does this person know or believe to be true?", "What core assumptions are driving their actions?"] },
        { label: "Care", key: "Care", color: "#c0392b", q: ["What does this person deeply value or care about?", "What are their primary motivations or fears?"] }
    ];

    const getAnswers = (key) => {
        const raw = data[key] ?? data[key.toLowerCase()] ?? [];
        if (Array.isArray(raw)) return raw.map(v => String(v ?? '').trim());
        if (typeof raw === 'string') return [raw.trim()];
        return [];
    };

    const isNodeActive = (key) => getAnswers(key).some(a => a.length > 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', boxSizing: 'border-box' }}>
            {/* 1. EXACT SCALE GRAPHICAL REPLICA */}
            <div style={{ width: '220px', height: '180px', margin: '0 auto', background: 'var(--bg-main)', borderRadius: '12px', border: '1px solid var(--border-subtle)', padding: '8px', boxSizing: 'border-box' }}>
                <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column-reverse', clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)', gap: '3px' }}>
                    {stepDef.map((node, i) => {
                        const active = isNodeActive(node.key);
                        return (
                            <div 
                                key={node.key} 
                                style={{ 
                                    flex: 1, 
                                    width: '100%', 
                                    backgroundColor: node.color, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justify: 'center', 
                                    color: '#ffffff', 
                                    fontSize: '11px', 
                                    fontWeight: '800', 
                                    textTransform: 'uppercase', 
                                    letterSpacing: '1px', 
                                    paddingTop: i === 2 ? '18px' : '0', 
                                    filter: active ? 'none' : 'grayscale(0.8)', 
                                    opacity: active ? 1 : 0.45 
                                }}
                            >
                                {node.label}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* 2. VERTICAL STACK OF NON-EMPTY RESPONSES ONLY */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%' }}>
                {stepDef.map(node => {
                    const ansList = getAnswers(node.key);
                    const filledEntries = node.q.map((qText, i) => ({ question: qText, answer: ansList[i] || '' })).filter(e => e.answer.length > 0);

                    if (filledEntries.length === 0) return null; // 🛡️ Hide empty fields completely

                    return (
                        <div key={node.key} style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderLeft: `4px solid ${node.color}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', color: node.color, borderBottom: '1px solid var(--border-subtle)', paddingBottom: '6px' }}>
                                {node.label} Layer
                            </div>
                            {filledEntries.map((entry, idx) => (
                                <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-muted)' }}>{entry.question}</div>
                                    <div style={{ fontSize: '13.5px', lineHeight: '1.5', color: 'var(--text-main)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{entry.answer}</div>
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ==========================================
// 🧵 CONNECTED ITEMS VISUALIZER COMPONENT (GRAPH & LOGS PARITY)
// ==========================================
const VB_X = -100;
const VB_Y = -100;
const VB_W = 600;
const VB_H = 600;

const toSvgX = (pxPct) => VB_X + (pxPct / 100.0) * VB_W;
const toSvgY = (pyPct) => VB_Y + (pyPct / 100.0) * VB_H;

const getPos = (index, total) => {
    const angleDeg = -90 + (index * (360 / Math.max(1, total)));
    const rad = (angleDeg * Math.PI) / 180;
    return { x: 50 + 35 * Math.cos(rad), y: 50 + 35 * Math.sin(rad), rad };
};

const THREAD_COLORS = [
    { accent: '#31b1c6', active: '#0891b2', bgTint: 'rgba(49, 177, 198, 0.2)' },
    { accent: '#10b981', active: '#059669', bgTint: 'rgba(16, 185, 129, 0.2)' },
    { accent: '#3b82f6', active: '#2563eb', bgTint: 'rgba(59, 130, 246, 0.2)' },
    { accent: '#f59e0b', active: '#d97706', bgTint: 'rgba(245, 158, 11, 0.2)' },
    { accent: '#ec4899', active: '#db2777', bgTint: 'rgba(236, 72, 153, 0.2)' },
    { accent: '#8b5cf6', active: '#7c3aed', bgTint: 'rgba(139, 92, 246, 0.2)' },
    { accent: '#14b8a6', active: '#0d9488', bgTint: 'rgba(20, 184, 166, 0.2)' },
    { accent: '#f97316', active: '#ea580c', bgTint: 'rgba(249, 115, 22, 0.2)' }
];

// 🧵 SMART BRAIDED HEMP ROPE RENDERER (BEZIERS FOR ADJACENT ITEMS & ROUNDED POLYLINES FOR SPANNING ITEMS)
const RopeLine = ({ x1, y1, x2, y2, ctrlX = null, ctrlY = null, waypoints = null, color, active, isCross = false }) => {
    const mainStroke = active ? color : 'rgba(156, 163, 175, 0.45)';
    const darkShadow = active ? 'rgba(17, 24, 39, 0.5)' : 'rgba(50, 50, 50, 0.3)';
    const lightHighlight = active ? 'rgba(255, 255, 255, 0.65)' : 'rgba(255, 255, 255, 0.3)';

    const width = isCross ? (active ? 5 : 3) : (active ? 6.5 : 4);

    let pathD = `M ${x1.toFixed(1)} ${y1.toFixed(1)} L ${x2.toFixed(1)} ${y2.toFixed(1)}`;

    if (waypoints && waypoints.length > 2) {
        // Render polyline with mathematically rounded corners using Quadratic Beziers (Q)
        const cornerRadius = 35.0; // The radius of the rounded elbows
        
        const dist = (p1, p2) => Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
        const getPtAtDist = (p1, p2, d) => {
            const l = dist(p1, p2);
            if (l === 0) return p1;
            const t = d / l;
            return { x: p1.x + (p2.x - p1.x) * t, y: p1.y + (p2.y - p1.y) * t };
        };

        const cmds = [`M ${waypoints[0].x.toFixed(1)} ${waypoints[0].y.toFixed(1)}`];

        for (let i = 1; i < waypoints.length - 1; i++) {
            const prev = waypoints[i - 1];
            const curr = waypoints[i];
            const next = waypoints[i + 1];

            // Constrain radius so it doesn't break short segments
            const maxR = Math.min(cornerRadius, dist(prev, curr) / 2.0, dist(curr, next) / 2.0);

            // Pull back from the sharp corner to start the curve
            const arcStart = getPtAtDist(curr, prev, maxR);
            // Push forward from the sharp corner to end the curve
            const arcEnd = getPtAtDist(curr, next, maxR);

            cmds.push(`L ${arcStart.x.toFixed(1)} ${arcStart.y.toFixed(1)}`);
            // Draw a smooth bezier curve around the sharp vertex
            cmds.push(`Q ${curr.x.toFixed(1)} ${curr.y.toFixed(1)}, ${arcEnd.x.toFixed(1)} ${arcEnd.y.toFixed(1)}`);
        }
        
        const lastPt = waypoints[waypoints.length - 1];
        cmds.push(`L ${lastPt.x.toFixed(1)} ${lastPt.y.toFixed(1)}`);
        
        pathD = cmds.join(' ');
        
    } else if (ctrlX !== null && ctrlY !== null) {
        // Bezier curve strictly for adjacent neighbor items
        pathD = `M ${x1.toFixed(1)} ${y1.toFixed(1)} Q ${ctrlX.toFixed(1)} ${ctrlY.toFixed(1)}, ${x2.toFixed(1)} ${y2.toFixed(1)}`;
    }

    return (
        <g>
            {/* Layer 1: Outer Depth Shadow */}
            <path d={pathD} fill="none" stroke={darkShadow} strokeWidth={width + 2} strokeLinecap="round" strokeLinejoin="round" />
            {/* Layer 2: Main Fiber Core */}
            <path d={pathD} fill="none" stroke={mainStroke} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" />
            {/* Layer 3: Dark Twisted Fiber Shadows */}
            <path d={pathD} fill="none" stroke={darkShadow} strokeWidth={Math.max(1, width - 1.2)} strokeDasharray="6 6" strokeDashoffset="0" strokeLinecap="round" strokeLinejoin="round" opacity="0.45" />
            {/* Layer 4: Interleaved Soft Strand Sheen Highlights */}
            <path d={pathD} fill="none" stroke={lightHighlight} strokeWidth={Math.max(1, width - 3.2)} strokeDasharray="6 6" strokeDashoffset="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.75" />
        </g>
    );
};

// Calculates dynamic routing: BEZIER for adjacent items, PARALLEL ANGLED POLYLINES FOR SPANNING ITEMS
const getCrossRoute = (pairIdx, i, j, totalNodes, nodesSvg, cx = 200, cy = 200) => {
    const p1 = nodesSvg[i];
    const p2 = nodesSvg[j];

    const diff = Math.abs(i - j);
    const stepDiff = Math.min(diff, totalNodes - diff);

    if (stepDiff === 1) {
        // Adjacent neighbor nodes: Bezier curve
        const mx = (p1.x + p2.x) / 2;
        const my = (p1.y + p2.y) / 2;
        const vx = mx - cx;
        const vy = my - cy;
        const dist = Math.sqrt(vx * vx + vy * vy);
        const targetDist = 265.0; // Pushed further out to clear nodes
        const scale = dist > 0 ? targetDist / dist : 1.0;
        return { ctrlX: cx + vx * scale, ctrlY: cy + vy * scale };
    }

    // Spanning cross connections (stepDiff >= 2): Straight line segments with angled radial elbows outside all nodes
    // PARALLEL CORRIDOR OFFSET: Each pair gets an incremental clearance radius (+ 25px per pair) so ropes never overlap!
    const baseClearance = 340.0 + ((stepDiff - 2) * 45.0);
    const pairOffset = (pairIdx || 0) * 25.0;
    const clearanceR = baseClearance + pairOffset;

    const pathIndices = [];
    if ((j - i + totalNodes) % totalNodes <= totalNodes / 2) {
        let curr = i;
        while (curr !== j) {
            pathIndices.push(curr);
            curr = (curr + 1) % totalNodes;
        }
        pathIndices.push(j);
    } else {
        let curr = i;
        while (curr !== j) {
            pathIndices.push(curr);
            curr = (curr - 1 + totalNodes) % totalNodes;
        }
        pathIndices.push(j);
    }

    const waypoints = [{ x: p1.x, y: p1.y }];
    for (let idx of pathIndices) {
        const radK = nodesSvg[idx].rad;
        waypoints.push({
            x: cx + clearanceR * Math.cos(radK),
            y: cy + clearanceR * Math.sin(radK)
        });
    }
    waypoints.push({ x: p2.x, y: p2.y });

    return { waypoints };
};

// ==========================================
// ⭕ DECISION CIRCLE VISUALIZER COMPONENT
// ==========================================
const DecisionCircleVisualizer = ({ scores }) => {
    if (!scores) return null;
    const data = typeof scores === 'object' ? scores : {};
    const title = data.decisionTitle || 'Untitled Decision';
    const stages = Array.isArray(data.stages) ? data.stages : [];
    const identityNotes = data.identityNotes || '';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', boxSizing: 'border-box' }}>
            {/* Accreditation Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '10px 14px' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    Framework by <strong style={{ color: 'var(--text-main)' }}>Jodi Crain</strong> (Centre-Out)
                </span>
                <a href="https://www.jodicrain.com.au/centre-out" target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', fontWeight: 'bold', color: '#31b1c6', textDecoration: 'none' }}>
                    jodicrain.com.au/centre-out ↗
                </a>
            </div>

            {/* Decision Focus Banner */}
            <div style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderLeft: '4px solid #31b1c6', borderRadius: '10px', padding: '12px 16px' }}>
                <div style={{ fontSize: '10px', fontWeight: '800', color: '#31b1c6', textTransform: 'uppercase', letterSpacing: '1px' }}>Decision Focus</div>
                <div style={{ fontSize: '15px', fontWeight: 'bold', color: 'var(--text-main)', marginTop: '2px' }}>{title}</div>
            </div>

            {/* Authentic Diagram Preview */}
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-subtle)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
                <div style={{ maxWidth: '240px', margin: '0 auto', borderRadius: '12px', overflow: 'hidden' }}>
                    <img 
                        src="/m4-finalproof-decision-circle.jpg" 
                        alt="Decision Circle" 
                        style={{ width: '100%', height: 'auto', display: 'block' }}
                        onError={(e) => { e.target.src = "/decision-circle.png"; }}
                    />
                </div>
            </div>

            {/* Stage Responses List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {stages.map((stg, i) => {
                    const ansList = (stg.answers || []).filter(a => (a || '').trim().length > 0);
                    if (ansList.length === 0) return null;
                    return (
                        <div key={stg.id || i} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-subtle)', borderLeft: '4px solid #31b1c6', borderRadius: '10px', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '800', color: '#31b1c6', textTransform: 'uppercase' }}>
                                Stage {i + 1}: {stg.label}
                            </div>
                            {ansList.map((ans, aIdx) => (
                                <div key={aIdx} style={{ fontSize: '13px', color: 'var(--text-main)', lineHeight: '1.4' }}>
                                    • {ans}
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>

            {identityNotes && (
                <div style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '12px 16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: '#f59e0b', textTransform: 'uppercase', marginBottom: '4px' }}>Leadership Formation</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-main)', lineHeight: '1.4' }}>{identityNotes}</div>
                </div>
            )}
        </div>
    );
};

const ConnectingThreadsVisualizer = ({ scores }) => {
    if (!scores) return null;
    const data = typeof scores === 'object' ? scores : {};
    const themeStr = data.theme || 'Central Theme';
    const threads = Array.isArray(data.threads) ? data.threads : [];

    const isThreadActive = (t) => 
        (t.label?.trim() && !t.label.startsWith('Connecting Thread ') && !t.label.startsWith('Connected Item ')) || 
        t.connection?.trim() || 
        t.insights?.trim() || 
        (t.crossConnections && t.crossConnections.length > 0);

    const activeThreads = threads.filter(isThreadActive);

    // Compute inter-item cross pairs
    const crossPairs = useMemo(() => {
        const pairs = [];
        const seen = new Set();
        threads.forEach((t, i) => {
            (t.crossConnections || []).forEach(targetIdx => {
                if (targetIdx !== i && targetIdx >= 0 && targetIdx < threads.length) {
                    const minIdx = Math.min(i, targetIdx);
                    const maxIdx = Math.max(i, targetIdx);
                    const key = `${minIdx}-${maxIdx}`;
                    if (!seen.has(key)) {
                        seen.add(key);
                        pairs.push({ source: minIdx, target: maxIdx });
                    }
                }
            });
        });
        return pairs;
    }, [threads]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', boxSizing: 'border-box' }}>
            {/* 1. CENTRAL FOCUS THEME BANNER */}
            <div style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderLeft: '4px solid #31b1c6', borderRadius: '10px', padding: '12px 16px' }}>
                <div style={{ fontSize: '10px', fontWeight: '800', color: '#31b1c6', textTransform: 'uppercase', letterSpacing: '1px' }}>Central Focus Theme</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--text-main)', marginTop: '2px' }}>{themeStr}</div>
            </div>

            {/* 2. RADIAL GRAPH VISUAL MAP */}
            {threads.length > 0 && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-subtle)', borderRadius: '12px', padding: '20px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '15px' }}>
                        Items Connection Map
                    </div>
                    <div className="ct-diagram-wrapper" style={{ position: 'relative', width: '100%', maxWidth: '340px', aspectRatio: '1 / 1', margin: '0 auto', overflow: 'visible' }}>
                        <svg viewBox="-100 -100 600 600" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible' }}>
                            {/* Spoke Ropes from Center to Orbit Nodes */}
                            {threads.map((t, i) => {
                                const pos = getPos(i, threads.length);
                                const colorObj = THREAD_COLORS[i % THREAD_COLORS.length];
                                const active = isThreadActive(t);
                                return (
                                    <RopeLine 
                                        key={`view-center-${t.id || i}`} 
                                        x1={toSvgX(50)} 
                                        y1={toSvgY(50)} 
                                        x2={toSvgX(pos.x)} 
                                        y2={toSvgY(pos.y)} 
                                        color={colorObj.accent} 
                                        active={active} 
                                    />
                                );
                            })}

                            {/* Inter-Item Cross-Ropes */}
                            {(() => {
                                const nodesSvg = threads.map((_, i) => {
                                    const pos = getPos(i, threads.length);
                                    return { x: toSvgX(pos.x), y: toSvgY(pos.y), rad: pos.rad };
                                });

                                return crossPairs.map((pair, pairIdx) => {
                                    const Ax = nodesSvg[pair.source].x;
                                    const Ay = nodesSvg[pair.source].y;
                                    const Bx = nodesSvg[pair.target].x;
                                    const By = nodesSvg[pair.target].y;
                                    const route = getCrossRoute(pairIdx, pair.source, pair.target, threads.length, nodesSvg, toSvgX(50), toSvgY(50));
                                    return (
                                        <RopeLine 
                                            key={`view-cross-${pair.source}-${pair.target}`} 
                                            x1={Ax} 
                                            y1={Ay} 
                                            x2={Bx} 
                                            y2={By} 
                                            ctrlX={route.ctrlX || null}
                                            ctrlY={route.ctrlY || null}
                                            waypoints={route.waypoints || null}
                                            color="#f59e0b" 
                                            active={true} 
                                            isCross={true} 
                                        />
                                    );
                                });
                            })()}
                        </svg>

                        {/* Center Theme Node */}
                        <div className="ct-center-node" style={{ zIndex: 10 }}>
                            <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-main)', textAlign: 'center', padding: '6px', wordBreak: 'break-word', lineHeight: '1.2' }}>
                                {themeStr}
                            </div>
                        </div>

                        {/* Orbit Nodes */}
                        {threads.map((t, i) => {
                            const pos = getPos(i, threads.length);
                            const active = isThreadActive(t);
                            const colorObj = THREAD_COLORS[i % THREAD_COLORS.length];
                            return (
                                <div 
                                    key={t.id || i} 
                                    className={`ct-orbit-node ${active ? 'active' : ''}`} 
                                    style={{ 
                                        position: 'absolute', 
                                        left: `${pos.x}%`, 
                                        top: `${pos.y}%`, 
                                        transform: 'translate(-50%, -50%)', 
                                        width: '75px', 
                                        height: '75px', 
                                        borderRadius: '50%', 
                                        background: active ? 'var(--card-bg)' : 'var(--trait-bg)', 
                                        border: active ? `3px solid ${colorObj.accent}` : '2px solid var(--text-muted)', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        textAlign: 'center', 
                                        padding: '6px', 
                                        fontSize: '9px', 
                                        fontWeight: active ? '800' : '600', 
                                        color: active ? 'var(--text-main)' : 'var(--text-muted)',
                                        boxSizing: 'border-box',
                                        wordBreak: 'break-word',
                                        zIndex: 10,
                                        boxShadow: '0 4px 12px var(--card-shadow)'
                                    }}
                                >
                                    {t.label?.trim() || `Item ${i + 1}`}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 3. ITEM DETAILS BREAKDOWN LIST */}
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-main)' }}>Connected Items ({activeThreads.length}):</div>
            {activeThreads.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {activeThreads.map((t, idx) => {
                        const origIdx = threads.findIndex(item => item === t);
                        const colorObj = THREAD_COLORS[(origIdx >= 0 ? origIdx : idx) % THREAD_COLORS.length];
                        const validCrossConnections = (t.crossConnections || []).filter(otherIdx => otherIdx >= 0 && otherIdx < threads.length && otherIdx !== origIdx);

                        return (
                            <div key={t.id || idx} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-subtle)', borderLeft: `4px solid ${colorObj.accent}`, borderRadius: '10px', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <div style={{ fontSize: '12px', fontWeight: '800', color: colorObj.accent, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    🔗 {t.label || `Connected Item ${idx + 1}`}
                                </div>
                                {t.connection && (
                                    <div style={{ fontSize: '12px', color: 'var(--text-main)', lineHeight: '1.4' }}>
                                        <strong>Connection to Central Theme:</strong> {t.connection}
                                    </div>
                                )}
                                
                                {/* 🔗 NEW: Displays specific Cross-Connection descriptions inside the My Logs Dashboard */}
                                {validCrossConnections.length > 0 && (
                                    <div style={{ marginTop: '6px', paddingTop: '8px', borderTop: '1px dashed var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-main)', textTransform: 'uppercase' }}>Cross-Connections:</div>
                                        {validCrossConnections.map(targetIdx => {
                                            const targetName = threads[targetIdx]?.label || `Item ${targetIdx + 1}`;
                                            const targetColor = THREAD_COLORS[targetIdx % THREAD_COLORS.length].accent;
                                            const note = t.crossNotes?.[targetIdx] || "Link established.";
                                            
                                            return (
                                                <div key={targetIdx} style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: '2px', background: 'var(--bg-main)', padding: '8px 12px', borderRadius: '6px' }}>
                                                    <strong style={{ color: targetColor }}>🔗 Relates to {targetName}:</strong>
                                                    <span style={{ fontStyle: 'italic', lineHeight: '1.4' }}>"{note}"</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {t.insights && (
                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.4', marginTop: '6px' }}>
                                        <strong>Insights &amp; Implications:</strong> {t.insights}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No active item connections logged.</div>
            )}
        </div>
    );
};

// ==========================================
// ⚡ 7-DAY ENERGY AUDIT VISUALIZER COMPONENT
// ==========================================
const EnergyAuditVisualizer = ({ scores }) => {
    if (!scores) return null;

    const data = typeof scores === 'object' ? scores : {};
    const weekData = data.weekData || {};

    let countUp = 0;
    let countDown = 0;
    const boostMoments = [];
    const drainMoments = [];

    Object.entries(weekData).forEach(([dateKey, dayBlocks]) => {
        if (Array.isArray(dayBlocks)) {
            dayBlocks.forEach(b => {
                if (b.type === 'up') {
                    countUp++;
                    if (b.title || b.who || b.energizing || b.met) {
                        boostMoments.push({ ...b, dateKey });
                    }
                }
                if (b.type === 'down') {
                    countDown++;
                    if (b.title || b.who || b.said || b.wanted) {
                        drainMoments.push({ ...b, dateKey });
                    }
                }
            });
        }
    });

    const totalScored = countUp + countDown;
    const positivityRatio = totalScored > 0 ? Math.round((countUp / totalScored) * 100) : 0;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', boxSizing: 'border-box' }}>
            {/* 1. EXECUTIVE RATIO METRIC CARDS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                <div style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderTop: '3px solid #10b981', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '9px', fontWeight: '800', color: '#10b981', textTransform: 'uppercase' }}>Boosts</div>
                    <div style={{ fontSize: '18px', fontWeight: '900', color: '#10b981' }}>+{countUp}</div>
                </div>
                <div style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderTop: '3px solid #ef4444', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '9px', fontWeight: '800', color: '#ef4444', textTransform: 'uppercase' }}>Drains</div>
                    <div style={{ fontSize: '18px', fontWeight: '900', color: '#ef4444' }}>-{countDown}</div>
                </div>
                <div style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderTop: '3px solid #31b1c6', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '9px', fontWeight: '800', color: '#31b1c6', textTransform: 'uppercase' }}>Positivity</div>
                    <div style={{ fontSize: '18px', fontWeight: '900', color: 'var(--text-main)' }}>{positivityRatio}%</div>
                </div>
            </div>

            {/* 2. ENERGY BOOST MOMENTS LIST */}
            {boostMoments.length > 0 && (
                <div style={{ background: 'rgba(16, 185, 129, 0.04)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: '#10b981', textTransform: 'uppercase' }}>
                        🌱 Energy Boost Moments ({boostMoments.length})
                    </div>
                    {boostMoments.map((b, i) => (
                        <div key={b.id || i} style={{ borderBottom: '1px solid var(--border-subtle)', paddingBottom: '8px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-main)' }}>
                                {b.slotLabel}: {b.title || 'Untitled Boost'}
                            </div>
                            {b.who && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}><strong>Who was there:</strong> {b.who}</div>}
                            {b.energizing && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}><strong>What made it energizing:</strong> {b.energizing}</div>}
                            {b.met && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}><strong>Need/value met:</strong> {b.met}</div>}
                        </div>
                    ))}
                </div>
            )}

            {/* 3. FREEZE-FRAME DRAIN MOMENTS LIST */}
            {drainMoments.length > 0 && (
                <div style={{ background: 'rgba(239, 68, 68, 0.04)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '800', color: '#ef4444', textTransform: 'uppercase' }}>
                        📷 Freeze-Frame Drain Moments ({drainMoments.length})
                    </div>
                    {drainMoments.map((b, i) => (
                        <div key={b.id || i} style={{ borderBottom: '1px solid var(--border-subtle)', paddingBottom: '8px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-main)' }}>
                                {b.slotLabel}: {b.title || 'Untitled Drain'}
                            </div>
                            {b.who && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}><strong>Who was there:</strong> {b.who}</div>}
                            {b.said && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}><strong>What was said/done:</strong> {b.said}</div>}
                            {b.wanted && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}><strong>What was wanted/unmet:</strong> {b.wanted}</div>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ==========================================
// 🔋 4-BATTERY CHECK-IN VISUALIZER COMPONENT
// ==========================================
const FourBatteriesVisualizer = ({ scores }) => {
    if (!scores) return null;
    const data = typeof scores === 'object' ? scores : {};
    const notes = data.notes || {};

    const bats = [
        { id: 'physical', name: 'Physical', icon: '🔋', val: data.physical, color: '#10b981' },
        { id: 'mental', name: 'Mental', icon: '🧠', val: data.mental, color: '#3b82f6' },
        { id: 'emotional', name: 'Emotional', icon: '❤️', val: data.emotional, color: '#ef4444' },
        { id: 'purpose', name: 'Purpose', icon: '✨', val: data.purpose, color: '#f59e0b' }
    ];

    const setVals = bats.map(b => b.val).filter(v => v !== null && v !== undefined && !isNaN(v));
    const avg = setVals.length > 0 ? Math.round(setVals.reduce((a, b) => a + b, 0) / setVals.length) : null;
    const segments = Array.from({ length: 20 }, (_, i) => (i + 1) * 5);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%', boxSizing: 'border-box' }}>
            {/* OVERALL RESERVE & CLINICAL CAPACITY INTERPRETATION */}
            <div style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '10px', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Overall Fuel Reserve</div>
                <div style={{ fontSize: '22px', fontWeight: '900', color: avg === null ? 'var(--text-muted)' : avg <= 25 ? '#ef4444' : avg <= 55 ? '#f59e0b' : '#10b981' }}>
                    {avg !== null ? `${avg}%` : '--%'}
                </div>
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: avg === null ? 'var(--text-muted)' : avg <= 25 ? '#ef4444' : avg <= 55 ? '#f59e0b' : '#10b981', marginTop: '2px' }}>
                    {avg === null ? '⚪ Unset' : avg <= 25 ? '⚠️ Critical Energy Depletion' : avg <= 55 ? '🟡 Moderate Reserves' : '🟢 Optimal Capacity'}
                </div>
            </div>

            {/* 20-SEGMENT MINI BATTERY CARDS WITH NOTES */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                {bats.map(b => {
                    const isUnset = b.val === null || b.val === undefined || isNaN(b.val);
                    const noteText = notes[b.id];
                    return (
                        <div key={b.id} style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderLeft: `3px solid ${isUnset ? 'var(--border-subtle)' : b.color}`, borderRadius: '8px', padding: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 'bold', color: 'var(--text-main)' }}>
                                <span>{b.icon} {b.name}</span>
                                <span style={{ color: isUnset ? 'var(--text-muted)' : b.color, whiteSpace: 'nowrap' }}>{isUnset ? '--%' : `${b.val}%`}</span>
                            </div>

                            {/* MINI 20-SEGMENT BATTERY BAR */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '3px', width: '100%' }}>
                                <div style={{ flex: 1, display: 'flex', gap: '1px', background: 'rgba(0,0,0,0.1)', padding: '2px', borderRadius: '4px', border: `1px solid ${isUnset ? 'var(--border-subtle)' : b.color}` }}>
                                    {segments.map(step => (
                                        <div 
                                            key={step} 
                                            style={{ 
                                                flex: 1, 
                                                height: '8px', 
                                                borderRadius: '1px', 
                                                background: !isUnset && b.val >= step ? b.color : 'transparent' 
                                            }} 
                                        />
                                    ))}
                                </div>
                                <div style={{ width: '3px', height: '6px', background: isUnset ? 'var(--border-subtle)' : b.color, borderRadius: '0 2px 2px 0' }} />
                            </div>

                            {noteText && (
                                <div style={{ fontSize: '10.5px', color: 'var(--text-muted)', lineHeight: '1.3' }}>
                                    {noteText}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {data.overallReflection && (
                <div style={{ fontSize: '12px', color: 'var(--text-main)', background: 'var(--trait-bg)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                    <strong>Session Action Plan:</strong> {data.overallReflection}
                </div>
            )}
        </div>
    );
};

// ==========================================
// 📊 WORKLOAD BANDWIDTH GAUGE VISUALIZER COMPONENT
// ==========================================
const BandwidthGaugeVisualizer = ({ scores }) => {
    if (!scores) return null;
    const data = typeof scores === 'object' ? scores : {};
    const score = data.bandwidthScore;
    const isUnset = score === null || score === undefined || isNaN(score);
    const active = Array.isArray(data.activeDrivers) ? data.activeDrivers : [];
    const driverDetails = data.driverDetails || {};

    const getScoreColor = (s) => {
        if (s === null || s === undefined || isNaN(s)) return 'var(--text-muted)';
        if (s <= 3) return '#ef4444';
        if (s <= 6) return '#f59e0b';
        return '#10b981';
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%', boxSizing: 'border-box' }}>
            {/* CAPACITY SCORE & INTERPRETIVE BAND */}
            <div style={{ background: 'var(--trait-bg)', border: '1px solid var(--border-subtle)', borderTop: `4px solid ${getScoreColor(score)}`, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '10px', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Capacity Score</div>
                <div style={{ fontSize: '24px', fontWeight: '900', color: getScoreColor(score) }}>
                    {isUnset ? '-- / 10' : `${score} / 10`}
                </div>
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: getScoreColor(score), marginTop: '2px' }}>
                    {isUnset ? '⚪ Unset' : score <= 3 ? '🚨 CRITICAL OVERLOAD' : score <= 6 ? '🟡 MODERATE CAPACITY' : '🟢 HIGH CAPACITY'}
                </div>
            </div>

            {/* ACTIVE LOAD DRIVERS WITH DETAILED NOTES PARITY */}
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-main)' }}>
                Active Workload Drivers ({active.length}):
            </div>
            {active.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {active.map(id => (
                        <div key={id} style={{ background: 'rgba(239, 68, 68, 0.05)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', padding: '8px 12px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '800', color: '#ef4444' }}>✓ {id.toUpperCase()}</div>
                            {driverDetails[id] && (
                                <div style={{ fontSize: '11px', color: 'var(--text-main)', marginTop: '2px' }}>{driverDetails[id]}</div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                    No workload drivers selected.
                </div>
            )}

            {data.actionNeeded && (
                <div style={{ fontSize: '12px', color: 'var(--text-main)', background: 'var(--trait-bg)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                    <strong>Operational Action Needed:</strong> {data.actionNeeded}
                </div>
            )}
        </div>
    );
};

// 📊 Custom Tooltip Component for Clinical Data Alignment
const ChartCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
        const dataPoint = payload[0].payload;
        const record = dataPoint.rawItem;
        
        if (!record) return null;

        const formattedTimestamp = new Date(record.date).toLocaleDateString(undefined, { 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        return (
            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-subtle)', padding: '14px', borderRadius: '12px', boxShadow: '0 10px 25px var(--card-shadow)', fontSize: '13px', zIndex: 10100, maxWidth: '280px' }}>
                <p style={{ margin: '0 0 6px 0', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.5px' }}>
                    {formattedTimestamp}
                </p>
                <p style={{ margin: '0 0 12px 0', color: 'var(--text-main)', fontWeight: '600', lineHeight: '1.4' }}>
                    {record.summary}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid var(--border-subtle)', paddingTop: '8px' }}>
                    {payload.map((entry, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{entry.name}:</span>
                            <span style={{ color: entry.color, fontWeight: '700' }}>{entry.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    return null;
};

export default function Home() {
    const { user, loadingAuth, orgName, licenseId, isPremium, history, profileData, refreshData } = useAuth();
    
    // 💸 INDIVIDUAL PAYPAL PRICING CONFIGURATION
    const PRO_MONTHLY_PRICE = 3;
    const PRO_ANNUAL_MONTHLY_EQUIV = 2; // $24 billed annually ($2/mo)

    const [billingCycle, setBillingCycle] = useState('annual');
    const [processingPayment, setProcessingPayment] = useState(false);
    const [revoking, setRevoking] = useState(false);

    // 🔀 DASHBOARD TAB & SUB-TAB STATE CONTROLLERS
    const [dashMainTab, setDashMainTab] = useState('summary'); // 'summary' | 'analytics' | 'logs' | 'account'
    const [summarySubTab, setSummarySubTab] = useState('jdr');  // 'jdr' | 'diagnosis' | 'protocol'
    const [logsSubTab, setLogsSubTab] = useState('reflective'); // 'reflective' | 'clinical'

    // 🔍 CARD SEARCH / FILTER QUERY STATE
    const [searchQuery, setSearchQuery] = useState('');

    // 1. ACTIVE TAB TRACKER (Defaulted to Supervision Tools)
    const [activeView, setActiveView] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const viewParam = params.get('view');
        const triageParam = params.get('triage');

        if (viewParam) return viewParam;
        if (triageParam === 'supervision') return 'supervision'; 
        return sessionStorage.getItem('onyx_home_view') || 'supervision';
    }); 

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);

    const [generatingPlan, setGeneratingPlan] = useState(false);
    const [planDuration, setPlanDuration] = useState(7);
    const [generatedPlan, setGeneratedPlan] = useState(null);

    // 2. CLINICAL TRIAGE MODAL (?triage=open)
    const [showTriage, setShowTriage] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const triageParam = params.get('triage');

        if (triageParam === 'open') return true;
        if (triageParam === 'closed' || triageParam === 'supervision') return false;
        
        if (params.get('tool') || params.get('view') || params.get('type')) return false;
        return false; //set to true to default to TriageModal
    });

    // 3. SUPERVISION REFLECTIVE PRACTICE MODAL (?triage=supervision)
    const [showSupervisionTriage, setShowSupervisionTriage] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('triage') === 'supervision';
    });

    // 🧠 HOLISTIC 90-DAY COMPILATION STATES
    const [holisticAnalysis, setHolisticAnalysis] = useState(null);
    const [analyzingHolistic, setAnalyzingHolistic] = useState(false);
    const [holisticLastGenerated, setHolisticLastGenerated] = useState(null);
    const [generatingHolisticPlan, setGeneratingHolisticPlan] = useState(false);
    const [holisticPlanDuration, setHolisticPlanDuration] = useState(7);
    const [generatedHolisticPlan, setGeneratedHolisticPlan] = useState(null);

    const recentHistory90Days = useMemo(() => {
        if (!history || !Array.isArray(history) || history.length === 0) return [];
        const ninetyDaysAgo = new Date();
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        return history.filter(h => h.date && new Date(h.date) >= ninetyDaysAgo);
    }, [history]);

    const [activeProtocol, setActiveProtocol] = useState(null);

    const fetchActiveProtocol = async () => {
        if (!user) return;
        try {
            const token = await user.getIdToken();
            const res = await fetch('/api/action-plan/active', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setActiveProtocol(data);
            }
        } catch (err) {
            console.error("Failed to load tracking checklists:", err);
        }
    };

    const [deleteConfirmationId, setDeleteConfirmationId] = useState(null);
    const [historyDeleteConfirmId, setHistoryDeleteConfirmId] = useState(null);

    // 🏢 B2B ENTERPRISE STOREFRONT PLUMBING STATES
    const [enterpriseSeats, setEnterpriseSeats] = useState(100);
    const [simulatedDomain, setSimulatedDomain] = useState('');
    const [validatedDomains, setValidatedDomains] = useState([]);
    const [procurementSuccess, setProcurementSuccess] = useState(false);

    const enterprisePricingCalculated = useMemo(() => {
        let costPerSeatMonthly = 3.00; 
        if (enterpriseSeats > 150 && enterpriseSeats <= 500) costPerSeatMonthly = 2.00;
        if (enterpriseSeats > 500) costPerSeatMonthly = 1.00; 

        const baseMonthlyTotal = enterpriseSeats * costPerSeatMonthly;
        const annualizedTotalWithDiscount = Math.floor((baseMonthlyTotal * 12) * 0.85); 

        return {
            perSeat: costPerSeatMonthly.toFixed(2),
            monthly: baseMonthlyTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
            annualized: annualizedTotalWithDiscount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
        };
    }, [enterpriseSeats]);

    const handleAddSimulatedDomain = (e) => {
        e.preventDefault();
        let cleanDomain = simulatedDomain.trim().toLowerCase().replace(/[^a-z0-9.-]/g, '');
        if (!cleanDomain || !cleanDomain.includes('.')) return;

        const publicBlacklist = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'proton.me'];
        if (publicBlacklist.includes(cleanDomain)) {
            alert(`⚠️ Security Block: "${cleanDomain}" is a public provider. Workspace licenses require dedicated corporate domains.`);
            return;
        }

        if (!validatedDomains.includes(cleanDomain)) {
            setValidatedDomains([...validatedDomains, cleanDomain]);
        }
        setSimulatedDomain('');
    };

    const [workspaceStats, setWorkspaceStats] = useState(null);

    const fetchWorkspaceStats = async () => {
        if (!user || !licenseId || orgName?.includes('Individual') || !profileData?.isCompanyAdmin) return;
        try {
            const token = await user.getIdToken();
            const res = await fetch('/api/enterprise/stats', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setWorkspaceStats(data);
            }
        } catch (err) {
            console.error("Failed to load live corporate metrics:", err);
        }
    };

    const protocolMetrics = useMemo(() => {
        if (!activeProtocol || !activeProtocol.days) return { hasActive: false, allCompleted: false };
        const hasActive = activeProtocol.status === 'active';
        const allCompleted = activeProtocol.days.every(d => d.completed);
        return { hasActive, allCompleted };
    }, [activeProtocol]);

    // 💳 INDIVIDUAL PAYPAL / PRO PURCHASE HANDLER
    const handleIndividualPurchase = async () => {
        if (!user) {
            try {
                await signInWithGoogle();
                return;
            } catch (err) {
                return alert("Google Sign-In is required to attach your license.");
            }
        }

        setProcessingPayment(true);
        try {
            const token = await user.getIdToken();
            // 1. Tell backend to generate a PayPal Subscription Hosted URL
            const res = await fetch('/api/paypal/create-subscription', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ billingCycle }) // 'monthly' or 'annual'
            });

            if (!res.ok) throw new Error("Failed to initialize PayPal checkout.");
            
            const data = await res.json();
            if (data.approveUrl) {
                // 2. Redirect user securely to PayPal's site to complete payment
                window.location.href = data.approveUrl; 
            } else {
                throw new Error("No checkout URL returned.");
            }
        } catch (err) {
            alert("Transaction could not be initialized. Please try again.");
            setProcessingPayment(false);
        }
    };

    // 🛑 REVOKE / CANCEL INDIVIDUAL PAYPAL LICENSE
    const handleRevokeLicense = async () => {
        if (!user || !window.confirm("Are you sure you want to revoke your Pro subscription? You will lose access to longitudinal tracking and advanced AI dynamics at the end of your term.")) return;

        setRevoking(true);
        try {
            const token = await user.getIdToken();
            const res = await fetch('/api/paypal/cancel', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                if (refreshData) await refreshData();
                alert("Subscription revoked successfully via PayPal.");
            } else {
                throw new Error("Revocation failed.");
            }
        } catch (err) {
            alert("Failed to cancel subscription. Please contact support.");
        }
        setRevoking(false);
    };

    useEffect(() => {
        sessionStorage.setItem('onyx_home_view', activeView);
        if (activeView === 'profile') {
            fetchActiveProtocol();
            fetchWorkspaceStats();
        }
    }, [activeView, user]);

    useEffect(() => {
        const syncSessionToken = async () => {
            if (!user) return;
            try {
                const token = await user.getIdToken();
                await fetch('/api/auth/session', {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ 
                        licenseId: licenseId || "none", 
                        appName: "onyx"
                    })
                });
            } catch (err) {
                console.error("Session sync failed:", err);
            }
        };
        syncSessionToken();
    }, [user, licenseId]);

    const initialNames = useMemo(() => {
        if (profileData?.name || user?.displayName) {
            const fetchedName = String(profileData?.name || user?.displayName || 'User');
            const firstSpaceIndex = fetchedName.indexOf(' ');
            if (firstSpaceIndex === -1) {
                return { first: fetchedName, last: '' };
            } else {
                return {
                    first: fetchedName.substring(0, firstSpaceIndex),
                    last: fetchedName.substring(firstSpaceIndex + 1)
                };
            }
        }
        return { first: '', last: '' };
    }, [profileData, user]);

    useEffect(() => {
        setFirstName(initialNames.first);
        setLastName(initialNames.last);
    }, [initialNames]);

    const groupedAnalytics = useMemo(() => {
        if (!history || !Array.isArray(history) || history.length === 0) return {};
        const groups = {};
        
        const toolCodeMap = {
            'sds6': 'SDS-6', 'obi16': 'OBI-16', 'dass21': 'DASS-21',
            'uwes17': 'UWES-17', 'copsoq': 'COPSOQ III', 'disc': 'DISC',
            'fivefactor': 'Big Five (OCEAN)', 'archetype': 'Archetypes', 'sapphire': 'Sapphire'
        };

        const sortedHistory = [...history].sort((a, b) => new Date(a.date) - new Date(b.date));

        sortedHistory.forEach(item => {
            if (!item?.chartData?.labels || !Array.isArray(item.chartData.labels)) return; 
            
            if (!groups[item.toolId]) {
                const codeName = toolCodeMap[item.toolId] || item.toolName || "Assessment";
                groups[item.toolId] = {
                    name: `Assessment ${codeName}`,
                    maxScale: item.chartData.maxScale || 100,
                    data: [],
                    lines: item.chartData.labels
                };
            }

            const dataPoint = {
                fullTimestamp: item.date, 
                rawItem: item 
            };

            item.chartData.labels.forEach((label, index) => {
                dataPoint[label] = item.chartData.values[index];
            });

            groups[item.toolId].data.push(dataPoint);
        });

        return groups;
    }, [history]);

    const chartColors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

    const handleGraphClick = (eventData) => {
        if (!eventData) return;

        const record = 
            eventData.rawItem || 
            eventData.payload?.rawItem || 
            eventData.activePayload?.[0]?.payload?.rawItem ||
            (eventData.points?.[0]?.payload?.rawItem);

        if (record) {
            setSelectedRecord(record);
            setAiAnalysis(null); 
            setGeneratedPlan(null);
        }
    };

    const safetyDirectives = [
        "Never do any harm or damage to the user, anyone, or anything the user interacts with.",
        "Never speculate or make any assumptions that are not immediately available in the data - you must not make anything up."
    ];

    const handleAnalyzeRecord = async () => {
        if (!selectedRecord || !user) return;
        setAnalyzing(true);
        setAiAnalysis(null);

        try {
            const token = await user.getIdToken();
            const res = await fetch('/api/analyze-record', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    toolName: selectedRecord.toolName || selectedRecord.tool,
                    scores: selectedRecord.chartData?.values || selectedRecord.scores,
                    labels: selectedRecord.chartData?.labels,
                    summary: selectedRecord.summary,
                    date: selectedRecord.date,
                    directives: safetyDirectives
                })
            });

            if (!res.ok) throw new Error("Analysis endpoint unavailable.");
            const data = await res.json();
            setAiAnalysis(data.analysis);
        } catch (err) {
            setAiAnalysis("AI analysis requires the /api/analyze-record backend endpoint to be configured with the new safety directives.");
        }
        setAnalyzing(false);
    };

    const HOLISTIC_CACHE_MS = 30 * 24 * 60 * 60 * 1000; // 30 Days in Milliseconds

    // 🚀 READ CACHED HOLISTIC DIAGNOSTIC ON LOAD (0 TOKENS SPENT)
    useEffect(() => {
        if (!user) return;
        const cacheKey = `holistic_diag_${user.uid}`;
        try {
            const cachedStr = localStorage.getItem(cacheKey);
            if (cachedStr) {
                const cached = JSON.parse(cachedStr);
                const age = Date.now() - (cached.timestamp || 0);
                if (cached.analysis && age < HOLISTIC_CACHE_MS) {
                    setHolisticAnalysis(cached.analysis);
                    setHolisticLastGenerated(cached.timestamp);
                }
            }
        } catch (e) {
            console.warn("Holistic cache read error:", e);
        }
    }, [user]);

    const handleAnalyzeHolistic = async () => {
        if (!isPremium) return setActiveView('pricing');
        if (recentHistory90Days.length === 0 || !user) return;
        
        setAnalyzingHolistic(true);

        try {
            const token = await user.getIdToken();
            const res = await fetch('/api/analyze-record', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    toolName: "90-Day Multi-Assessment Baseline",
                    date: new Date().toISOString(),
                    summary: `Synthesized analysis compiled from ${recentHistory90Days.length} distinct timelines.`,
                    scores: recentHistory90Days.map(h => ({ tool: h.tool, data: h.chartData?.values || h.scores })),
                    directives: safetyDirectives
                })
            });

            if (!res.ok) throw new Error("Holistic server analysis offline.");
            const data = await res.json();
            const resultText = data.analysis || "Holistic compilation summary generated successfully.";
            const now = Date.now();
            
            setHolisticAnalysis(resultText);
            setHolisticLastGenerated(now);

            // 🛡️ SAVE TO LOCAL STORAGE WITH TIMESTAMP
            try {
                const cacheKey = `holistic_diag_${user.uid}`;
                localStorage.setItem(cacheKey, JSON.stringify({
                    timestamp: now,
                    analysis: resultText
                }));
            } catch (e) {}

        } catch (err) {
            setHolisticAnalysis("Holistic compilation summary generated successfully.");
        }
        setAnalyzingHolistic(false);
    };

    const handleGenerateHolisticPlan = async () => {
        if (!isPremium) return setActiveView('pricing');
        if (recentHistory90Days.length === 0 || !user) return;

        if (protocolMetrics.hasActive) {
            if (!protocolMetrics.allCompleted) return;

            try {
                const token = await user.getIdToken();
                await fetch(`/api/action-plan/update/${activeProtocol.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ status: "archived" })
                });
            } catch (e) {
                console.error("Failed programmatic archiving step:", e);
            }
        }

        setGeneratingHolisticPlan(true);
        setGeneratedHolisticPlan(null);

        try {
            const token = await user.getIdToken();
            const contextData = recentHistory90Days.map(h => ({
                toolName: h.toolName || h.tool,
                summary: h.summary
            }));

            const res = await fetch('/api/action-plan/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    toolName: "Holistic Cross-Assessment Protocol",
                    summary: `Consolidated risk-mitigation sprint using ${recentHistory90Days.length} active data contexts.`,
                    scores: {},
                    recentContext: contextData,
                    duration: holisticPlanDuration,
                    directives: safetyDirectives
                })
            });

            if (!res.ok) throw new Error("Failed generation.");
            const data = await res.json();
            setGeneratedHolisticPlan(data);
            setActiveProtocol(data);
            
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                document.querySelector('.app-layout')?.scrollTo({ top: 0, behavior: 'smooth' });
            }, 80); 
        } catch (err) {
            alert("Failed to synthesize holistic action plan template.");
        }
        setGeneratingHolisticPlan(false);
    };

    const recentContextData = useMemo(() => {
        if (!history || !selectedRecord) return [];
        const ninetyDaysAgo = new Date();
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
        
        return history
            .filter(h => new Date(h.date) >= ninetyDaysAgo && h.id !== selectedRecord.id)
            .map(h => ({ toolName: h.toolName || h.tool, summary: h.summary }));
    }, [history, selectedRecord]);

    const handleGenerateActionPlan = async () => {
        if (!selectedRecord || !user) return;
        setGeneratingPlan(true);
        setGeneratedPlan(null);

        try {
            const token = await user.getIdToken();
            const res = await fetch('/api/action-plan/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    toolName: selectedRecord.toolName || selectedRecord.tool,
                    summary: selectedRecord.summary,
                    scores: selectedRecord.chartData?.values || selectedRecord.scores,
                    recentContext: recentContextData,
                    duration: planDuration,
                    directives: safetyDirectives
                })
            });

            if (!res.ok) throw new Error("Failed to generate plan.");
            const data = await res.json();
            setGeneratedPlan(data);
        } catch (err) {
            alert("Failed to generate Action Plan. Ensure backend is running.");
        }
        setGeneratingPlan(false);
    };

    const handleToggleDay = async (dayIndex) => {
        if (!activeProtocol || !activeProtocol.days) return;

        const updatedDays = activeProtocol.days.map((day, idx) => 
            idx === dayIndex ? { ...day, completed: !day.completed } : day
        );
        
        setActiveProtocol({ ...activeProtocol, days: updatedDays });

        try {
            const token = await user.getIdToken();
            await fetch(`/api/action-plan/update/${activeProtocol.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ days: updatedDays })
            });
        } catch (err) {
            console.error("Autosave synchronization failed:", err);
        }
    };

    const handleCompleteAllDays = async () => {
        if (!activeProtocol || !activeProtocol.days) return;

        const updatedDays = activeProtocol.days.map(d => ({ ...d, completed: true }));
        setActiveProtocol({ ...activeProtocol, days: updatedDays });

        try {
            const token = await user.getIdToken();
            await fetch(`/api/action-plan/update/${activeProtocol.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ days: updatedDays })
            });
        } catch (err) {
            console.error("Bulk processing save error:", err);
        }
    };

    const handleDeleteActiveProtocol = async () => {
        if (!activeProtocol) return;

        try {
            const token = await user.getIdToken();
            const res = await fetch(`/api/action-plan/update/${activeProtocol.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ status: "deleted" })
            });
            if (res.ok) {
                setActiveProtocol(null);
                setGeneratedHolisticPlan(null);
                setDeleteConfirmationId(null); 
            }
        } catch (err) {
            console.error("Failed to securely terminate protocol parameters.");
        }
    };

    const isFormChanged = useMemo(() => {
        return firstName.trim() !== initialNames.first || lastName.trim() !== initialNames.last;
    }, [firstName, lastName, initialNames]);

    const hasHighPriority = useMemo(() => {
        if (!history || !Array.isArray(history) || history.length === 0) return false;
        const now = new Date();
        const ninetyDaysAgo = new Date(now.setDate(now.getDate() - 90));
        
        return history.some(item => {
            if (!item || !item.date || !item.summary) return false;
            const itemDate = new Date(item.date);
            if (isNaN(itemDate.getTime()) || itemDate < ninetyDaysAgo) return false;
            
            const summaryStr = String(item.summary).toLowerCase();
            return summaryStr.includes('elevated') || summaryStr.includes('severe') || summaryStr.includes('high');
        });
    }, [history]);

    const displayHeaderName = profileData?.name || user?.displayName || 'User';

    const handleLogin = async () => {
        try { await signInWithGoogle(); } catch (error) { alert("Failed to log in."); }
    };

    const handleLogout = () => {
        signOut(auth);
        setActiveView('tools');
    };

    const handleUpdateProfile = async () => {
        if (!isFormChanged) return;
        setSavingProfile(true);
        try {
            const cleanFirst = String(firstName || '').trim().substring(0, 50);
            const cleanLast = String(lastName || '').trim().substring(0, 50);

            const token = await user.getIdToken();
            const res = await fetch('/api/user/profile', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ firstName: cleanFirst, lastName: cleanLast })
            });

            if (!res.ok) throw new Error("Server rejected update");
            if (refreshData) await refreshData();

            const btn = document.getElementById('saveProfileBtn');
            if (btn) {
                const originalText = btn.innerText;
                btn.innerText = '✓ Saved Successfully';
                btn.style.backgroundColor = '#10b981';
                setTimeout(() => {
                    if (document.getElementById('saveProfileBtn')) {
                        btn.innerText = originalText;
                        btn.style.backgroundColor = 'var(--text-main)';
                    }
                }, 2500);
            }

        } catch (e) {
            alert("Failed to update profile securely. Please try again.");
        }
        setSavingProfile(false);
    };

    const handleDeleteHistory = async (assessmentId) => {
        try {
            const token = await user.getIdToken();
            const res = await fetch(`/api/user/history/${assessmentId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (res.ok) {
                if (refreshData) await refreshData();
                setSelectedRecord(null); 
                setHistoryDeleteConfirmId(null);
            } else {
                throw new Error("Failed server deletion");
            }
        } catch (e) {
            alert("Failed to delete record.");
        }
    };

    // 📸 VISUAL SNAPSHOT & COMPREHENSIVE DATA MATRIX SHARE ENGINE
    const handleShareRecord = async (record) => {
        if (!record) return;

        const formattedDate = record.date 
            ? new Date(record.date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
            : 'Unknown Date';
        
        const toolTitle = record.tool || record.toolName || "Assessment Record";
        const cleanTitle = toolTitle.replace(/[^a-zA-Z0-9]/g, '_');
        const fileName = `${cleanTitle}_${new Date(record.date || Date.now()).toISOString().split('T')[0]}.png`;

        // 1. Build Full Comprehensive Text Matrix Payload
        let textLines = [
            `SUPERVISION RECORD: ${toolTitle.toUpperCase()}`,
            `Date: ${formattedDate}`,
            `\nSUMMARY:\n${record.summary || 'No summary recorded.'}`
        ];

        const scores = record.scores;

        if (record.toolId === 'fourbatteries' && scores) {
            textLines.push(`\nBATTERY FUEL RESERVES:`);
            if (scores.physical !== null && scores.physical !== undefined) textLines.push(`• Physical Battery: ${scores.physical}%`);
            if (scores.mental !== null && scores.mental !== undefined) textLines.push(`• Mental Battery: ${scores.mental}%`);
            if (scores.emotional !== null && scores.emotional !== undefined) textLines.push(`• Emotional Battery: ${scores.emotional}%`);
            if (scores.purpose !== null && scores.purpose !== undefined) textLines.push(`• Purpose Battery: ${scores.purpose}%`);

            const notes = scores.notes || {};
            const activeNotes = Object.entries(notes).filter(([_, text]) => typeof text === 'string' && text.trim().length > 0);
            if (activeNotes.length > 0) {
                textLines.push(`\nBATTERY NOTES:`);
                activeNotes.forEach(([k, text]) => textLines.push(`• ${k.charAt(0).toUpperCase() + k.slice(1)} Note: ${text.trim()}`));
            }

            if (scores.overallReflection?.trim()) {
                textLines.push(`\nSESSION ACTION PLAN:\n${scores.overallReflection.trim()}`);
            }
        } else if (record.toolId === 'bandwidthgauge' && scores) {
            textLines.push(`\nWORKLOAD CAPACITY:`);
            textLines.push(`• Capacity Score: ${scores.bandwidthScore !== null && scores.bandwidthScore !== undefined ? `${scores.bandwidthScore}/10` : 'Unset'}`);
            if (Array.isArray(scores.activeDrivers) && scores.activeDrivers.length > 0) {
                textLines.push(`• Active Demand Drivers: ${scores.activeDrivers.join(', ')}`);
                const details = scores.driverDetails || {};
                Object.entries(details).forEach(([drvKey, text]) => {
                    if (typeof text === 'string' && text.trim()) {
                        textLines.push(`  - ${drvKey}: ${text.trim()}`);
                    }
                });
            }
            if (scores.actionNeeded?.trim()) {
                textLines.push(`\nOPERATIONAL ACTION NEEDED:\n${scores.actionNeeded.trim()}`);
            }
        } else if (record.toolId === 'decisioncircle' && scores) {
            textLines.push(`\nDECISION FOCUS: ${scores.decisionTitle || 'Untitled Decision'}`);
            textLines.push(`Framework by Jodi Crain (https://www.jodicrain.com.au/centre-out)`);
            const stgs = scores.stages || [];
            stgs.forEach((stg, i) => {
                const filled = (stg.answers || []).filter(a => (a || '').trim().length > 0);
                if (filled.length > 0) {
                    textLines.push(`\nSTAGE ${i + 1}: ${stg.label.toUpperCase()}`);
                    filled.forEach(a => textLines.push(`• ${a}`));
                }
            });
            if (scores.identityNotes?.trim()) {
                textLines.push(`\nLEADERSHIP FORMATION:\n${scores.identityNotes.trim()}`);
            }
        } else if (record.toolId === 'connectingthreads' && scores) {
            const theme = scores.theme || "Unspecified Focus";
            const threadsData = Array.isArray(scores.threads) ? scores.threads : [];
            
            const activeThreads = threadsData.filter(t => 
                (t.label?.trim() && !t.label.startsWith('Connecting Thread ') && !t.label.startsWith('Connected Item ')) || 
                t.connection?.trim() || 
                t.insights?.trim() || 
                (t.crossConnections && t.crossConnections.length > 0)
            );
            
            textLines.push(`\nCENTRAL THEME: ${theme}`);
            textLines.push(`\nCONNECTED ITEMS (${activeThreads.length}):`);
            
            activeThreads.forEach((t, idx) => {
                const origIdx = threadsData.findIndex(item => item === t);
                textLines.push(`\n• ITEM: ${t.label || `Item ${idx + 1}`}`);
                if (t.connection) textLines.push(`  Connection: ${t.connection.trim()}`);
                
                const cross = t.crossConnections || [];
                if (cross.length > 0) {
                    textLines.push(`  Cross-Connections:`);
                    cross.forEach(targetIdx => {
                        if (targetIdx >= 0 && targetIdx < threadsData.length && targetIdx !== origIdx) {
                            const targetName = threadsData[targetIdx].label || `Item ${targetIdx + 1}`;
                            const note = t.crossNotes?.[targetIdx] ? t.crossNotes[targetIdx].trim() : 'Link established.';
                            textLines.push(`    - Relates to ${targetName}: "${note}"`);
                        }
                    });
                }
                if (t.insights) textLines.push(`  Insights: ${t.insights.trim()}`);
            });
        } else if (record.chartData?.labels && Array.isArray(record.chartData.labels)) {
            textLines.push(`\nRAW DATA MATRIX:`);
            record.chartData.labels.forEach((label, idx) => {
                const val = record.chartData.values?.[idx];
                textLines.push(`• ${label}: ${val !== undefined ? val : 'N/A'}`);
            });
        } else if (scores && typeof scores === 'object') {
            textLines.push(`\nRAW DATA MATRIX:`);
            Object.entries(scores).forEach(([k, v]) => {
                if (typeof v !== 'object') textLines.push(`• ${k}: ${v}`);
            });
        }

        textLines.push(`\nShared via Professional Supervision Toolkit`);
        const fullShareText = textLines.join('\n');

        // 2. Load html2canvas engine if needed
        if (!window.html2canvas) {
            await new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
                script.onload = resolve;
                script.onerror = reject;
                document.head.appendChild(script);
            });
        }

        const cardElement = document.getElementById('selected-record-card');
        if (!cardElement) return;

        try {
            // 🎯 Detect exact computed solid background color (prevents transparent email wash-out)
            const computedStyle = window.getComputedStyle(cardElement);
            let solidBg = computedStyle.backgroundColor;
            if (!solidBg || solidBg === 'transparent' || solidBg === 'rgba(0, 0, 0, 0)') {
                solidBg = document.documentElement.getAttribute('data-theme') === 'light' ? '#ffffff' : '#141416';
            }

            // 3. Render Card to 2x Retina Canvas with a 100% OPAQUE background
            const canvas = await window.html2canvas(cardElement, {
                scale: 2,
                backgroundColor: solidBg,
                useCORS: true,
                logging: false,
                ignoreElements: (element) => element.tagName === 'BUTTON' || element.classList.contains('hide-on-print')
            });

            // 4. Attach image file & text body to share sheet
            canvas.toBlob(async (blob) => {
                if (!blob) return;

                const imageFile = new File([blob], fileName, { type: 'image/png' });
                const shareSubject = `Supervision Log: ${toolTitle} (${formattedDate})`;

                if (navigator.canShare && navigator.canShare({ files: [imageFile] })) {
                    try {
                        await navigator.share({
                            title: shareSubject,
                            text: fullShareText,
                            files: [imageFile]
                        });
                        return;
                    } catch (err) {
                        if (err.name === 'AbortError') return;
                    }
                }

                // 5. Desktop Fallback: Downloads PNG & pre-fills Mail Client with full text
                const downloadLink = document.createElement('a');
                downloadLink.href = URL.createObjectURL(blob);
                downloadLink.download = fileName;
                downloadLink.click();

                const mailtoUrl = `mailto:?subject=${encodeURIComponent(shareSubject)}&body=${encodeURIComponent(`${fullShareText}\n\n[Note: Visual snapshot chart "${fileName}" downloaded to attach to this email.]`)}`;
                window.location.href = mailtoUrl;
            }, 'image/png');

        } catch (e) {
            console.error("Visual snapshot generation error:", e);
        }
    };

    const icons = {
        // --- CLINICAL TOOL ICONS ---
        archetype: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
        disc: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="3" x2="12" y2="21"/><line x1="3" y1="12" x2="21" y2="12"/></svg>,
        sliders: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>,
        pulse: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
        battery: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="6" width="18" height="12" rx="2" ry="2"/><line x1="23" y1="13" x2="23" y2="11"/><line x1="6" y1="10" x2="6" y2="14"/></svg>,
        trending: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
        copsoq: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><line x1="9" y1="6" x2="9.01" y2="6"/><line x1="15" y1="6" x2="15.01" y2="6"/><line x1="9" y1="10" x2="9.01" y2="10"/><line x1="15" y1="10" x2="15.01" y2="10"/><line x1="9" y1="14" x2="9.01" y2="14"/><line x1="15" y1="14" x2="15.01" y2="14"/><line x1="9" y1="18" x2="15" y2="18"/></svg>,
        sds6: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4M12 20v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M20 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/><circle cx="12" cy="12" r="4"/></svg>,
        sapphire: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h12l4 6-10 12L2 9z"/><line x1="11" y1="3" x2="8" y2="9"/><line x1="13" y1="3" x2="16" y2="9"/><line x1="2" y1="9" x2="22" y2="9"/><line x1="12" y1="21" x2="8" y2="9"/><line x1="12" y1="21" x2="16" y2="9"/></svg>,
        workinggenius: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,

        // --- SUPERVISION TOOL ICONS ---
        compass: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
        target: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/><line x1="12" y1="1" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="23"/><line x1="1" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="23" y2="12"/></svg>,
        traffic: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="2" width="12" height="20" rx="4"/><circle cx="12" cy="6" r="2" fill="currentColor"/><circle cx="12" cy="12" r="2" fill="currentColor"/><circle cx="12" cy="18" r="2" fill="currentColor"/></svg>,
        complexity: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="3" r="2"/><circle cx="21" cy="9" r="2"/><circle cx="18" cy="19" r="2"/><circle cx="6" cy="19" r="2"/><circle cx="3" cy="9" r="2"/><path d="M12 5l7.5 3.5M12 5l-7.5 3.5M12 5l4 12M12 5l-4 12M19.5 10.5l-2.5 7M19.5 10.5L7.5 18M4.5 10.5l2.5 7M4.5 10.5L16.5 18M7.5 19h9"/></svg>,
        threeys: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6.5"/><circle cx="12" cy="12" r="3"/></svg>,
        cec: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="3"/><circle cx="18" cy="6" r="3"/><circle cx="12" cy="18" r="3"/><line x1="8.5" y1="7.5" x2="15.5" y2="7.5"/><line x1="7.5" y1="8.5" x2="10.5" y2="15.5"/><line x1="16.5" y1="8.5" x2="13.5" y2="15.5"/></svg>,
        stepinside: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>,
        morethanwords: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
        focuscards: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="14" height="16" rx="2"/><path d="M6 2h14a2 2 0 0 1 2 2v14"/></svg>,
        bridge: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19V9"/><path d="M20 19V9"/><path d="M2 9h20"/><path d="M4 19c0-4.4 3.6-8 8-8s8 3.6 8 8"/><line x1="8" y1="11" x2="8" y2="19"/><line x1="16" y1="11" x2="16" y2="19"/></svg>,
        stickingpoints: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 12l10 10 10-10L12 2z"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="9"/><line x1="12" y1="15" x2="12" y2="22"/><line x1="2" y1="12" x2="9" y2="12"/><line x1="15" y1="12" x2="22" y2="12"/></svg>,
        circleofviewpoints: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><circle cx="12" cy="3" r="2"/><circle cx="21" cy="12" r="2"/><circle cx="12" cy="21" r="2"/><circle cx="3" cy="12" r="2"/></svg>,
        usedtothink: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M8 10h8"/><path d="M8 14h5"/></svg>,
        claimsupport: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20"/><rect x="6" y="6" width="12" height="12" rx="2"/></svg>,
        tugofwar: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12h20"/><circle cx="12" cy="12" r="2.5" fill="currentColor"/><path d="m6 8-4 4 4 4"/><path d="m18 8 4 4-4 4"/></svg>,
        trueforwho: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>,
        headlines: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8v4h-8z"/></svg>,
        generatesort: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><path d="M10 6.5h4"/><path d="M17.5 10v4"/><path d="M10 17.5h4"/></svg>,
        energyaudit: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>,
        fourbatteries: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="16" height="10" rx="2"/><line x1="22" y1="11" x2="22" y2="13"/><line x1="6" y1="11" x2="6" y2="13"/><line x1="10" y1="11" x2="10" y2="13"/></svg>,
        bandwidthgauge: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 14l3-3"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/></svg>,
        connected: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" fill="currentColor"/><circle cx="4" cy="6" r="2"/><circle cx="20" cy="6" r="2"/><circle cx="4" cy="18" r="2"/><circle cx="20" cy="18" r="2"/><line x1="12" y1="12" x2="4" y2="6" strokeDasharray="2,2"/><line x1="12" y1="12" x2="20" y2="6" strokeDasharray="2,2"/><line x1="12" y1="12" x2="4" y2="18" strokeDasharray="2,2"/><line x1="12" y1="12" x2="20" y2="18" strokeDasharray="2,2"/></svg>//,
        //decisioncircle: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><path d="M12 2v6"/><path d="M12 16v6"/><path d="m4.93 4.93 4.24 4.24"/><path d="m14.83 14.83 4.24 4.24"/></svg>
    };

    const clinicalTools = [
        { id: 'archetype', category: 'Behavioral', title: 'Personality Archetypes', desc: 'Discover your professional Keirsey temperament and core desires.', icon: icons.archetype },
        { id: 'disc', category: 'Behavioral', title: 'DISC Assessment', desc: 'Map your communication preferences and adaptation to stress.', icon: icons.disc },
        { id: 'fivefactor', category: 'Psychometric', title: 'Big Five (OCEAN)', desc: 'Measure your psychological footprint across the 5 major traits.', icon: icons.sliders },
        { id: 'dass21', category: 'Clinical', title: 'DASS-21 Scale', desc: 'Assess recent levels of depression, anxiety, and stress.', icon: icons.pulse },
        { id: 'obi16', category: 'Workplace', title: 'OBI-16 Burnout', desc: 'Evaluate professional exhaustion and workplace disengagement.', icon: icons.battery },
        { id: 'uwes17', category: 'Workplace', title: 'UWES-17 Engagement', desc: 'Measure your work vigor, dedication, and task absorption.', icon: icons.trending },
        { id: 'copsoq', category: 'Workplace', title: 'COPSOQ III Worklife', desc: 'Map structural work demands, pace, and team support networks.', icon: icons.copsoq },
        { id: 'sds6', category: 'Wellbeing', title: 'Spiritual Dryness', desc: 'Evaluate your spiritual vitality and connectedness.', icon: icons.sds6 },
        { id: 'sapphire', category: 'Executive', title: 'Psychometric Matrix', desc: 'Discover your executive cognitive OS and operational style.', icon: icons.sapphire },
        { 
            id: 'workinggenius', 
            category: 'Operational', 
            title: 'Working Genius', 
            desc: 'Map your innate areas of operational joy, competency, and frustration.', 
            externalUrl: 'https://www.workinggenius.com/', 
            externalUrlLabel: 'Take Working Genius\nAssessment ↗', 
            icon: icons.workinggenius 
        }
    ];

    const supervisionTools = [
        { 
            id: 'target', 
            category: 'Proximity Check', 
            title: 'Target Tool', 
            desc: 'Visualize the distance between your current state and your professional center.', 
            icon: icons.target,
            urlParams: '&labels=personal,relationships,work' 
        },
        { 
            id: 'traffic', 
            category: 'State Assessment', 
            title: 'Traffic Lights', 
            desc: 'Gage your current feeling/risk level to prioritize where support is needed.', 
            icon: icons.traffic,
            urlParams: '&labels=personal,relationships,work' 
        },
        { id: 'compass', category: 'Decision Support', title: 'Compass Points', desc: 'Map your Needs, Excitements, Worries, and Suggestions to identify next steps.', icon: icons.compass, externalUrl: 'https://pz.harvard.edu/resources/compass-points', externalUrlLabel: 'Project Zero ↗' },
        { id: 'complexity', category: 'Systemic Analysis', title: 'Complexity Assessment', desc: 'Break a situation down into manageable variables and interactions.', icon: icons.complexity, externalUrl: 'https://pz.harvard.edu/resources/parts-purposes-complexities', externalUrlLabel: 'Project Zero ↗' },
        { id: '3ys', category: 'Purpose Alignment', title: 'The 3Ys', desc: 'Find the Why by connecting personal issues to local and global significance.', icon: icons.threeys, externalUrl: 'https://pz.harvard.edu/resources/3-whys', externalUrlLabel: 'Project Zero ↗' },
        { id: 'cec', category: 'Synthesis Tool', title: 'Connect, Extend, Challenge', desc: 'Synthesize new information and identify the growing edges of your practice.', icon: icons.cec, externalUrl: 'https://pz.harvard.edu/resources/connect-extend-challenge', externalUrlLabel: 'Project Zero ↗' },
        { id: 'stepinside', category: 'Perspective Shifting', title: 'Step Inside', desc: 'Inhabit another perspective to gain deep empathy or self-insight.', icon: icons.stepinside, externalUrl: 'https://pz.harvard.edu/resources/step-inside', externalUrlLabel: 'Project Zero ↗' },
        { id: 'morethanwords', category: 'Non-Verbal Insight', title: 'More Than Words', desc: 'Bypass cognitive filters to express the complex through visual metaphors.', icon: icons.morethanwords },
        { id: 'focuscards', category: 'Session Preparation', title: 'Focus Cards', desc: 'Review card constraints across three thematic clusters to pinpoint your agenda items.', icon: icons.focuscards },
        { id: 'bridge', category: 'Learning & Shift', title: '3-2-1 Bridge', desc: 'Map how your understanding shifts before and after a supervision session or learning event.', icon: icons.bridge, externalUrl: 'https://pz.harvard.edu/resources/3-2-1-bridge', externalUrlLabel: 'Project Zero ↗' },
        { id: 'stickingpoints', category: 'Systemic Analysis', title: 'Sticking Points', desc: 'Deconstruct messy controversies into Facts, Values, Interests, and Policies.', icon: icons.stickingpoints, externalUrl: 'https://pz.harvard.edu/resources/sticking-points', externalUrlLabel: 'Project Zero ↗' },
        { id: 'circleofviewpoints', category: 'Perspective Shifting', title: 'Circle of Viewpoints', desc: 'Explore a case by inhabiting diverse stakeholder perspectives.', icon: icons.circleofviewpoints, externalUrl: 'https://pz.harvard.edu/resources/circle-of-viewpoints', externalUrlLabel: 'Project Zero ↗' },
        { id: 'usedtothink', category: 'Learning & Shift', title: 'I Used to Think... Now I Think', desc: 'Track how your clinical judgment or understanding evolves over time.', icon: icons.usedtothink, externalUrl: 'https://pz.harvard.edu/resources/i-used-think-now-i-think', externalUrlLabel: 'Project Zero ↗' },
        { id: 'claimsupport', category: 'Synthesis Tool', title: 'Claim, Support, Question', desc: 'Test diagnostic claims using evidence and probing questions.', icon: icons.claimsupport, externalUrl: 'https://pz.harvard.edu/resources/claim-support-question', externalUrlLabel: 'Project Zero ↗' },
        { id: 'tugofwar', category: 'Decision Support', title: 'Tug-of-War', desc: 'Untangle complex ethical tensions and competing forces in a dilemma.', icon: icons.tugofwar, externalUrl: 'https://pz.harvard.edu/resources/tug-war', externalUrlLabel: 'Project Zero ↗' },
        { id: 'trueforwho', category: 'Perspective & Humility', title: 'True for Who?', desc: 'Examine policy rules, claims, or best practices through cultural and power lenses.', icon: icons.trueforwho, externalUrl: 'https://pz.harvard.edu/resources/true-who', externalUrlLabel: 'Project Zero ↗' },
        { id: 'headlines', category: 'Synthesis & Debrief', title: 'Headlines', desc: 'Capture the essence of a session breakthrough or situation in a punchy headline.', icon: icons.headlines, externalUrl: 'https://pz.harvard.edu/resources/headlines', externalUrlLabel: 'Project Zero ↗' },
        { id: 'generatesort', category: 'Systemic Analysis', title: 'Generate, Sort, Connect, Elaborate', desc: 'Map out complex professional scenarios, student/client cases, or team initiatives.', icon: icons.generatesort, externalUrl: 'https://pz.harvard.edu/resources/generate-sort-connect-elaborate', externalUrlLabel: 'Project Zero ↗' },
        { id: 'energyaudit', category: 'State Assessment', title: 'Weekly Energy Audit', desc: 'Audit your calendar as a lab report. Score energy boosts vs. drains and freeze-frame unmet needs.', icon: icons.energyaudit },
        { id: 'fourbatteries', category: 'State Assessment', title: '4-Battery Check-In', desc: 'Gauge your physical, mental, emotional, and purpose fuel levels in 30 seconds.', icon: icons.fourbatteries },
        { id: 'bandwidthgauge', category: 'State Assessment', title: 'Workload Bandwidth Gauge', desc: 'Measure operational capacity (0-10) and isolate specific workload demand drivers.', icon: icons.bandwidthgauge },
        { id: 'connected', category: 'Systemic Analysis', title: 'Connected Items', desc: 'Nominate a central theme and map connected items with inter-item cross-linking capabilities.', icon: icons.connected }//,
        //{ 
        //    id: 'decisioncircle', 
        //    category: 'Discernment & Action', 
        //    title: 'The Decision Circle', 
        //    desc: 'Six stages of faithful discernment moving from noticing to release. By Jodi Crain.', 
        //    icon: icons.decisioncircle,
        //    externalUrl: 'https://www.jodicrain.com.au/centre-out',
        //    externalUrlLabel: 'Jodi Crain ↗'
        //}
    ];

    // 🔤 ALPHABETICAL SORTING & DUAL-FIELD (TITLE + DESC) SEARCH FILTER
    const displayedTools = useMemo(() => {
        const sourceList = activeView === 'tools' ? clinicalTools : supervisionTools;
        const sortedList = [...sourceList].sort((a, b) => a.title.localeCompare(b.title));

        if (!searchQuery.trim()) return sortedList;

        const q = searchQuery.toLowerCase().trim();
        return sortedList.filter(tool => 
            (tool.title && tool.title.toLowerCase().includes(q)) || 
            (tool.desc && tool.desc.toLowerCase().includes(q))
        );
    }, [activeView, searchQuery, clinicalTools, supervisionTools]);

    if (loadingAuth) {
        return (
            <div className="tool-loader-overlay" style={{ backdropFilter: 'none' }}>
                <div className="tool-loader-ring"></div>
                <div className="tool-loader-text" style={{ color: 'var(--text-main)', letterSpacing: '4px' }}>Onyx App</div>
                <div className="tool-loader-subtext" style={{ marginTop: '12px' }}>Establishing secure context...</div>
            </div>
        );
    }

    return (
        <div className="disc-wrapper">
            <header className="slim-header" style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 30px', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <a 
                        href="https://professionalsupervision.me" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                    >
                        <img 
                            src="/icon.png" 
                            alt="Onyx Logo" 
                            style={{ 
                                height: '32px', 
                                width: '32px', 
                                borderRadius: '50%', 
                                objectFit: 'cover', 
                                border: '1px solid var(--border-subtle)',
                                transition: 'transform 0.2s ease' 
                            }}
                            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                        />
                    </a>
                </div>
                
                {user ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {/* 🚀 2. MY DASHBOARD ICON BUTTON */}
                        <button 
                            onClick={() => setActiveView('profile')} 
                            className="header-icon-btn"
                            title="My Dashboard"
                            style={{ position: 'relative' }}
                        >
                            <ChartLineUpIcon size="18px" />
                            {hasHighPriority && (
                                <span style={{ position: 'absolute', top: '2px', right: '2px', width: '8px', height: '8px', background: '#ef4444', borderRadius: '50%' }}></span>
                            )}
                        </button>

                        {/* 🚀 1. AVATAR (User Name text completely removed) */}
                        <img 
                            src={user.photoURL} alt="Profile" referrerPolicy="no-referrer"
                            onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayHeaderName)}&background=005fb0&color=fff`; }}
                            style={{ width: '34px', height: '34px', borderRadius: '50%', border: '1px solid var(--border-subtle)', cursor: 'pointer' }} 
                            onClick={() => setActiveView('profile')}
                            title={displayHeaderName}
                        />

                        {/* 🚀 SIGN OUT ICON BUTTON */}
                        <button 
                            onClick={handleLogout} 
                            className="header-icon-btn"
                            title="Sign Out"
                        >
                            <SignOutIcon size="18px" />
                        </button>
                    </div>
                ) : (
                    /* 🚀 SIGN IN ICON BUTTON */
                    <button 
                        onClick={handleLogin} 
                        className="header-icon-btn"
                        title="Sign In"
                    >
                        <SignInIcon size="18px" />
                    </button>
                )}
            </header>

            <div className="app-layout" style={{ paddingTop: '80px', maxWidth: '1100px', margin: '0 auto', paddingBottom: '60px' }}>
                
                {(activeView === 'tools' || activeView === 'supervision') && (
                    <>
                        <div style={{ textAlign: 'center', marginBottom: '60px', padding: '0 20px' }}>
                            <h1 style={{ fontSize: '2.8rem', marginBottom: '15px', color: 'var(--text-main)', letterSpacing: '-0.5px' }}>
                                {activeView === 'tools' ? 'Clinical Diagnostics' : 'Reflective Practice'}
                            </h1>
                            <p style={{ color: 'var(--text-muted)', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto 30px auto' }}>
                                {activeView === 'tools' 
                                    ? 'Select an assessment below to measure your baseline.' 
                                    : 'Select a framework below to process your daily practice.'}
                                {!user && <span> <strong>Sign in</strong> to sync your historical data securely.</span>}
                            </p>

                            <div style={{ display: 'flex', justifyContent: 'center', background: 'var(--trait-bg)', borderRadius: '30px', padding: '6px', maxWidth: '400px', margin: '0 auto 30px auto', border: '1px solid var(--border-subtle)' }}>
                                <button 
                                    onClick={() => setActiveView('tools')}
                                    style={{ flex: 1, padding: '10px 20px', borderRadius: '24px', border: 'none', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', background: activeView === 'tools' ? 'var(--text-main)' : 'transparent', color: activeView === 'tools' ? 'var(--bg-main)' : 'var(--text-muted)', transition: 'all 0.2s' }}
                                >
                                    Clinical Tools
                                </button>
                                <button 
                                    onClick={() => setActiveView('supervision')}
                                    style={{ flex: 1, padding: '10px 20px', borderRadius: '24px', border: 'none', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer', background: activeView === 'supervision' ? 'var(--text-main)' : 'transparent', color: activeView === 'supervision' ? 'var(--bg-main)' : 'var(--text-muted)', transition: 'all 0.2s' }}
                                >
                                    Supervision Tools
                                </button>
                            </div>

                            <div style={{ display: 'inline-block', position: 'relative' }}>
                                <button 
                                    onClick={() => window.location.href = activeView === 'tools' ? '/?triage=open' : '/?triage=supervision'}
                                    style={{ background: 'transparent', border: '1px dashed var(--text-muted)', color: 'var(--text-main)', padding: '10px 24px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', transition: 'border-color 0.2s' }}
                                    onMouseOver={e => e.currentTarget.style.borderColor = 'var(--text-main)'}
                                    onMouseOut={e => e.currentTarget.style.borderColor = 'var(--text-muted)'}
                                >
                                    {activeView === 'tools' ? '❓ Help me choose a clinical tool' : '❓ Guide my reflective practice'}
                                </button>
                                {hasHighPriority && activeView === 'tools' && (
                                    <span style={{ position: 'absolute', top: '-4px', right: '-4px', width: '12px', height: '12px', background: '#ef4444', borderRadius: '50%', border: '2px solid var(--bg-main)' }}></span>
                                )}
                            </div>

                            {/* 🔍 REAL-TIME DUAL-FIELD SEARCH BAR */}
                            <div style={{ maxWidth: '480px', margin: '25px auto 0 auto', position: 'relative' }}>
                                <input 
                                    type="text"
                                    placeholder={activeView === 'tools' ? "🔍 Search clinical tools by name or description..." : "🔍 Search supervision tools by name or description..."}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 40px 12px 18px',
                                        borderRadius: '24px',
                                        border: '1px solid var(--border-subtle)',
                                        background: 'var(--card-bg)',
                                        color: 'var(--text-main)',
                                        fontSize: '13.5px',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        boxShadow: '0 2px 8px var(--card-shadow)',
                                        transition: 'all 0.2s ease'
                                    }}
                                />
                                {searchQuery && (
                                    <button 
                                        onClick={() => setSearchQuery('')}
                                        style={{
                                            position: 'absolute',
                                            right: '14px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'transparent',
                                            border: 'none',
                                            color: 'var(--text-muted)',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                        }}
                                        title="Clear search"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* 🚀 ALPHABETICALLY SORTED & FILTERED GRID */}
                        <div 
                            className="premium-grid animate-fade" 
                            style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                                gap: '20px', 
                                gridAutoRows: 'max-content', 
                                alignItems: 'start' 
                            }}
                        >
                            {displayedTools.length === 0 ? (
                                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)', fontSize: '14px' }}>
                                    No tools found matching "<strong>{searchQuery}</strong>". Try clearing your search query.
                                </div>
                            ) : (
                                displayedTools.map(tool => (
                                <div 
                                    key={tool.id} 
                                    onClick={() => window.location.href = `/?tool=${tool.id}${tool.urlParams || ''}`} 
                                    className="premium-card" 
                                    style={{ 
                                        textAlign: 'left', 
                                        cursor: 'pointer', 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        justify: 'space-between',
                                        minHeight: '180px',
                                        height: '100%',
                                        padding: '18px 20px',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                            <div className="premium-card-icon" style={{ margin: 0 }}>{tool.icon}</div>
                                            {tool.externalUrl && (
                                                <a 
                                                    href={tool.externalUrl} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    onClick={(e) => e.stopPropagation()} 
                                                    style={{ 
                                                        fontSize: '10px', 
                                                        fontWeight: 'bold', 
                                                        color: '#10b981', 
                                                        background: 'rgba(16, 185, 129, 0.12)', 
                                                        border: '1px solid rgba(16, 185, 129, 0.3)', 
                                                        padding: '5px 9px', 
                                                        borderRadius: '10px', 
                                                        textDecoration: 'none', 
                                                        transition: 'all 0.2s ease',
                                                        whiteSpace: 'pre-line',
                                                        textAlign: 'right',
                                                        lineHeight: '1.2',
                                                        maxWidth: '130px'
                                                    }}
                                                    onMouseOver={e => {
                                                        e.currentTarget.style.background = '#10b981';
                                                        e.currentTarget.style.color = '#ffffff';
                                                    }}
                                                    onMouseOut={e => {
                                                        e.currentTarget.style.background = 'rgba(16, 185, 129, 0.12)';
                                                        e.currentTarget.style.color = '#10b981';
                                                    }}
                                                >
                                                    {tool.externalUrlLabel || 'Take Test ↗'}
                                                </a>
                                            )}
                                        </div>
                                        <div className="premium-card-tag" style={{ marginBottom: '4px' }}>{tool.category}</div>
                                        <h3 className="premium-card-title" style={{ margin: '0 0 6px 0', fontSize: '1.2rem', lineHeight: '1.3' }}>{tool.title}</h3>
                                        <p className="premium-card-desc" style={{ margin: 0, fontSize: '13px', lineHeight: '1.4', color: 'var(--text-muted)' }}>{tool.desc}</p>
                                    </div>
                                    
                                    {user && (
                                        <div className="premium-card-status" style={{ marginTop: '14px', paddingTop: '8px', borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#10b981', fontWeight: '600' }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                            Auto-Sync Active
                                        </div>
                                    )}
                                </div>
                            )))}
                        </div>
                    </>
                )}

                {activeView === 'profile' && !user && (
                    <div className="animate-fade" style={{ textAlign: 'center', padding: '100px 20px', maxWidth: '600px', margin: '0 auto' }}>
                        <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
                        <h2 style={{ color: 'var(--text-main)', marginBottom: '15px' }}>Dashboard Locked</h2>
                        <p style={{ color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: '30px' }}>
                            You need to be signed in to access your historical dashboard, view saved results, and utilize AI analysis.
                        </p>
                        <button onClick={handleLogin} style={{ background: 'var(--text-main)', color: 'var(--bg-main)', border: 'none', padding: '12px 30px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px', marginBottom: '20px' }}>
                            Sign In with Google
                        </button>
                        <div style={{ marginTop: '20px' }}>
                            <button onClick={() => setActiveView('tools')} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                                ← Return to Toolkit
                            </button>
                        </div>
                    </div>
                )}

                {/* 📅 SECTION: ACTIVE PROTOCOLS CHECKLIST */}
                {activeProtocol && activeProtocol.days && (
                    <div className="trait-section" style={{ padding: '30px', marginBottom: '30px', position: 'relative', background: 'var(--card-bg)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '12px', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700' }}>📅 Active Protocol Progress</h3>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <button 
                                    onClick={handleCompleteAllDays} 
                                    style={{ background: 'var(--btn-primary-bg)', color: 'var(--text-main)', border: '1px solid var(--border-subtle)', padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s' }}
                                >
                                    Complete All
                                </button>

                                {deleteConfirmationId === activeProtocol.id ? (
                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', background: 'var(--btn-primary-bg)', padding: '4px 8px', borderRadius: '8px', border: '1px solid var(--border-subtle)', animation: 'fadeIn 0.2s ease' }}>
                                        <span style={{ fontSize: '11px', color: '#ef4444', fontWeight: '700', padding: '0 4px' }}>Reset Engine?</span>
                                        <button 
                                            onClick={handleDeleteActiveProtocol}
                                            style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                                        >
                                            Yes, Reset
                                        </button>
                                        <button 
                                            onClick={() => setDeleteConfirmationId(null)}
                                            style={{ background: 'var(--card-bg)', color: 'var(--text-main)', border: '1px solid var(--border-subtle)', padding: '6px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <button 
                                        onClick={() => setDeleteConfirmationId(activeProtocol.id)} 
                                        style={{ background: 'transparent', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)', padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s ease' }}
                                        onMouseOver={e => e.currentTarget.style.background = 'rgba(239,68,68,0.05)'}
                                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                        Delete Plan
                                    </button>
                                )}
                            </div>
                        </div>

                        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '25px', fontStyle: 'italic', lineHeight: '1.5' }}>
                            <strong>Rationale:</strong> {activeProtocol.rationale}
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {activeProtocol.days.map((day, idx) => (
                                <label 
                                    key={idx} 
                                    style={{ 
                                        display: 'flex', 
                                        alignItems: 'flex-start', 
                                        gap: '14px', 
                                        padding: '16px 20px', 
                                        background: day.completed ? 'var(--trait-bg)' : 'var(--bg-main)', 
                                        border: '1px solid var(--border-subtle)', 
                                        borderRadius: '10px', 
                                        cursor: 'pointer',
                                        opacity: day.completed ? 0.65 : 1,
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    <input 
                                        type="checkbox" 
                                        checked={!!day.completed} 
                                        onChange={() => handleToggleDay(idx)}
                                        style={{ width: '18px', height: '18px', marginTop: '3px', cursor: 'pointer', accentColor: '#10b981' }}
                                    />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: day.completed ? 'var(--text-muted)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>
                                            Day {day.day}
                                        </span>
                                        <span style={{ fontSize: '14px', fontWeight: '500', color: day.completed ? 'var(--text-muted)' : 'var(--text-main)', textDecoration: day.completed ? 'line-through' : 'none', lineHeight: '1.5', display: 'block' }}>
                                            {day.task}
                                        </span>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {activeView === 'profile' && user && (
                    <div className="animate-fade" style={{ padding: '0 20px', maxWidth: '950px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                        
                        {/* 🚀 TOP USER HEADER */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                <img 
                                    src={user.photoURL} alt="Profile" referrerPolicy="no-referrer"
                                    onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayHeaderName)}&background=005fb0&color=fff`; }}
                                    style={{ width: '70px', height: '70px', borderRadius: '50%', border: '2px solid var(--text-main)' }} 
                                />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <h1 style={{ margin: '0 0 5px 0', fontSize: 'clamp(1.4rem, 4vw, 1.8rem)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {displayHeaderName}
                                    </h1>
                                    <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {user.email}
                                    </p>
                                </div>
                            </div>

                            <button 
                                onClick={() => setActiveView('tools')} 
                                className="header-icon-btn"
                                title="Close Dashboard"
                            >
                                <WindowCloseIcon size="18px" />
                            </button>
                        </div>

                        {/* 🚀 3. MAIN TOP TABS */}
                        <div className="dashboard-main-tabs">
                            <button 
                                className={`dashboard-main-tab-btn ${dashMainTab === 'summary' ? 'active' : ''}`}
                                onClick={() => setDashMainTab('summary')}
                            >
                                Summary
                            </button>
                            <button 
                                className={`dashboard-main-tab-btn ${dashMainTab === 'analytics' ? 'active' : ''}`}
                                onClick={() => setDashMainTab('analytics')}
                            >
                                Analytics & Trends
                            </button>
                            <button 
                                className={`dashboard-main-tab-btn ${dashMainTab === 'logs' ? 'active' : ''}`}
                                onClick={() => setDashMainTab('logs')}
                            >
                                My Logs
                            </button>
                            <button 
                                className={`dashboard-main-tab-btn ${dashMainTab === 'account' ? 'active' : ''}`}
                                onClick={() => setDashMainTab('account')}
                            >
                                Account
                            </button>
                        </div>

                        {/* =========================================================
                           TAB 1: SUMMARY (WITH 3 VERTICAL LEFT SUB-TABS)
                           ========================================================= */}
                        {dashMainTab === 'summary' && (
                            <div className="dashboard-subtab-layout animate-fade">
                                <div className="dashboard-subtabs-sidebar">
                                    <button 
                                        className={`dashboard-subtab-btn ${summarySubTab === 'jdr' ? 'active' : ''}`}
                                        onClick={() => setSummarySubTab('jdr')}
                                    >
                                        JD-R Balance
                                    </button>
                                    <button 
                                        className={`dashboard-subtab-btn ${summarySubTab === 'diagnosis' ? 'active' : ''}`}
                                        onClick={() => setSummarySubTab('diagnosis')}
                                    >
                                        Holistic Diagnosis
                                    </button>
                                    <button 
                                        className={`dashboard-subtab-btn ${summarySubTab === 'protocol' ? 'active' : ''}`}
                                        onClick={() => setSummarySubTab('protocol')}
                                    >
                                        Intervention Protocol
                                    </button>
                                </div>

                                <div className="dashboard-subtab-content">
                                    {/* Sub-Tab 1: JDR Balance */}
                                    {summarySubTab === 'jdr' && (
                                        <div className="animate-fade">
                                            <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center', opacity: 0.5, fontSize: '12px' }}>Loading Radar...</div>}>
                                                <JdrRadarWidget history={history} isPremium={isPremium} />
                                            </Suspense>
                                        </div>
                                    )}

                                    {/* Sub-Tab 2: Holistic Diagnosis */}
                                    {summarySubTab === 'diagnosis' && (
                                        <div className="animate-fade">
                                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-subtle)', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                                <div>
                                                    <span style={{ fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px', color: 'var(--text-muted)', background: 'var(--trait-bg)', padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--border-subtle)' }}>
                                                        Cross-Vector Intelligence
                                                    </span>
                                                    <h4 style={{ margin: '10px 0 6px 0', fontSize: '1.3rem', fontWeight: '800', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span>✨</span> Holistic Diagnostics
                                                    </h4>
                                                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                                                        Instruct the AI engine to run cross-vector mathematical trend checks across your active baseline matrices.
                                                    </p>
                                                </div>

                                                {/* Output Card or Empty Placeholder */}
                                                {holisticAnalysis ? (
                                                    <div className="animate-fade" style={{ background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '10px', padding: '18px' }}>
                                                        <h5 style={{ margin: '0 0 8px 0', color: '#3b82f6', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '800' }}>
                                                            ✨ Holistic Synthesis Result
                                                        </h5>
                                                        <p style={{ margin: 0, fontSize: '13.5px', lineHeight: '1.6', color: 'var(--text-main)' }}>
                                                            {holisticAnalysis}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div style={{ background: 'var(--bg-main)', border: '1px dashed var(--border-subtle)', borderRadius: '10px', padding: '20px', textAlign: 'center' }}>
                                                        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)' }}>
                                                            No holistic synthesis generated yet. Execute below to analyze your active baseline matrices.
                                                        </p>
                                                    </div>
                                                )}

                                                {/* 🚀 FOOTER BAR: Always visible (Button + Timestamp Note) */}
                                                <div style={{
                                                    paddingTop: '15px',
                                                    borderTop: '1px solid var(--border-subtle)',
                                                    display: 'flex',
                                                    justify: 'space-between',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: '12px'
                                                }}>
                                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span>🕒</span>
                                                        <span>
                                                            {holisticLastGenerated 
                                                                ? `Analysis generated on ${new Date(holisticLastGenerated).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                                                                : 'Analysis ready to run'}
                                                        </span>
                                                    </div>

                                                    <button
                                                        onClick={handleAnalyzeHolistic}
                                                        disabled={analyzingHolistic || generatingHolisticPlan}
                                                        style={{
                                                            background: 'var(--trait-bg)',
                                                            border: '1px solid var(--border-subtle)',
                                                            borderRadius: '20px',
                                                            padding: '6px 14px',
                                                            fontSize: '11px',
                                                            fontWeight: '700',
                                                            color: 'var(--text-main)',
                                                            cursor: (analyzingHolistic || generatingHolisticPlan) ? 'not-allowed' : 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            transition: 'all 0.2s ease',
                                                            opacity: (analyzingHolistic || generatingHolisticPlan) ? 0.6 : 1
                                                        }}
                                                        title="Execute AI synthesis"
                                                        onMouseOver={e => e.currentTarget.style.borderColor = 'var(--text-muted)'}
                                                        onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
                                                    >
                                                        <RefreshIcon size="12px" />
                                                        <span>
                                                            {analyzingHolistic 
                                                                ? 'Processing Matrix...' 
                                                                : holisticAnalysis 
                                                                    ? 'Refresh Analysis' 
                                                                    : 'Run Synthesis'}
                                                        </span>
                                                    </button>
                                                </div>

                                            </div>
                                        </div>
                                    )}

                                    {/* Sub-Tab 3: Intervention Protocol */}
                                    {summarySubTab === 'protocol' && (
                                        <div className="animate-fade">
                                            {/* Active Checklist */}
                                            {activeProtocol && activeProtocol.days && (
                                                <div className="trait-section" style={{ padding: '24px', marginBottom: '25px', background: 'var(--card-bg)' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '12px', marginBottom: '15px' }}>
                                                        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700' }}>📅 Active Protocol Progress</h3>
                                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                            <button 
                                                                onClick={handleCompleteAllDays} 
                                                                style={{ background: 'var(--btn-primary-bg)', color: 'var(--text-main)', border: '1px solid var(--border-subtle)', padding: '6px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                                                            >
                                                                Complete All
                                                            </button>

                                                            {deleteConfirmationId === activeProtocol.id ? (
                                                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', background: 'var(--btn-primary-bg)', padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border-subtle)' }}>
                                                                    <span style={{ fontSize: '11px', color: '#ef4444', fontWeight: '700' }}>Reset?</span>
                                                                    <button onClick={handleDeleteActiveProtocol} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer' }}>Yes</button>
                                                                    <button onClick={() => setDeleteConfirmationId(null)} style={{ background: 'var(--card-bg)', color: 'var(--text-main)', border: '1px solid var(--border-subtle)', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer' }}>Cancel</button>
                                                                </div>
                                                            ) : (
                                                                <button 
                                                                    onClick={() => setDeleteConfirmationId(activeProtocol.id)} 
                                                                    style={{ background: 'transparent', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)', padding: '6px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}
                                                                >
                                                                    Delete
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                        {activeProtocol.days.map((day, idx) => (
                                                            <label 
                                                                key={idx} 
                                                                style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 16px', background: day.completed ? 'var(--trait-bg)' : 'var(--bg-main)', border: '1px solid var(--border-subtle)', borderRadius: '8px', cursor: 'pointer', opacity: day.completed ? 0.65 : 1 }}
                                                            >
                                                                <input type="checkbox" checked={!!day.completed} onChange={() => handleToggleDay(idx)} style={{ width: '16px', height: '16px', marginTop: '2px', accentColor: '#10b981' }} />
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    <span style={{ display: 'block', fontSize: '10px', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Day {day.day}</span>
                                                                    <span style={{ fontSize: '13px', fontWeight: '500', color: day.completed ? 'var(--text-muted)' : 'var(--text-main)', textDecoration: day.completed ? 'line-through' : 'none' }}>{day.task}</span>
                                                                </div>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Generator Box */}
                                            <div style={{ background: 'var(--bg-main)', border: '1px solid var(--border-subtle)', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                                <div>
                                                    <h5 style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: 'bold', color: 'var(--text-main)' }}>📅 Configure New Intervention Protocol</h5>
                                                    <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>Configure a task sprint optimized to target your overlapping clinical strains.</p>
                                                </div>
                                                
                                                <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                    <select 
                                                        value={holisticPlanDuration} 
                                                        onChange={e => setHolisticPlanDuration(Number(e.target.value))}
                                                        disabled={generatingHolisticPlan || analyzingHolistic}
                                                        style={{ padding: '10px', borderRadius: '8px', border: '1px solid var(--border-subtle)', background: 'var(--bg-main)', color: 'var(--text-main)', fontWeight: 'bold', fontSize: '12px', width: '110px' }}
                                                    >
                                                        <option value={7}>7 Days</option>
                                                        <option value={14}>14 Days</option>
                                                        <option value={21}>21 Days</option>
                                                    </select>

                                                    <button 
                                                        onClick={handleGenerateHolisticPlan}
                                                        disabled={generatingHolisticPlan || analyzingHolistic || (protocolMetrics.hasActive && !protocolMetrics.allCompleted)}
                                                        style={{ flex: 1, background: (protocolMetrics.hasActive && !protocolMetrics.allCompleted) ? 'var(--btn-primary-bg)' : '#10b981', color: (protocolMetrics.hasActive && !protocolMetrics.allCompleted) ? 'var(--text-muted)' : '#fff', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '12px' }}
                                                    >
                                                        {generatingHolisticPlan ? 'Synthesizing...' : (protocolMetrics.hasActive && !protocolMetrics.allCompleted) ? 'Protocol Active' : 'Generate Plan'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* =========================================================
                           TAB 2: ANALYTICS & TRENDS
                           ========================================================= */}
                        {dashMainTab === 'analytics' && (
                            <div className="trait-section animate-fade" style={{ padding: '30px', marginBottom: '30px', position: 'relative', overflow: 'hidden' }}>
                                <h3 style={{ margin: '0 0 20px 0', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '10px' }}>Analytics & Trends</h3>
                                
                                {!isPremium ? (
                                    <div style={{ position: 'relative', minHeight: '300px' }}>
                                        <div style={{ filter: 'blur(8px)', opacity: 0.3, pointerEvents: 'none', height: '300px', background: 'repeating-linear-gradient(45deg, var(--border-subtle), var(--border-subtle) 10px, transparent 10px, transparent 20px)' }}></div>
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', background: 'var(--card-bg)', padding: '30px', borderRadius: '12px', border: '1px solid var(--border-subtle)', boxShadow: '0 10px 30px var(--card-shadow)', width: '90%', maxWidth: '400px', zIndex: 10 }}>
                                            <div style={{ fontSize: '32px', marginBottom: '10px' }}>📈</div>
                                            <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-main)' }}>Unlock Longitudinal Tracking</h4>
                                            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px', lineHeight: 1.5 }}>Visualize your burnout recovery, stress adaptation, and wellbeing trends over time with Onyx Premium.</p>
                                            <button className="age-btn" onClick={() => setActiveView('pricing')} style={{ width: '100%', background: 'var(--text-main)', color: 'var(--bg-main)', border: 'none' }}>Upgrade to Premium</button>
                                        </div>
                                    </div>
                                ) : Object.keys(groupedAnalytics).length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
                                        {Object.entries(groupedAnalytics).map(([toolId, chartData]) => (
                                            <div key={toolId} className="animate-fade">
                                                {/* 🚀 Explicitly Titled Assessment Header */}
                                                <h4 style={{ margin: '0 0 5px 0', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-main)', fontWeight: '800' }}>
                                                    {chartData.name}
                                                </h4>
                                                <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '15px' }}>Tap any node to view exact historical scores & AI insights.</p>
                                                
                                                <div style={{ width: '100%', height: 300, background: 'var(--bg-main)', borderRadius: '12px', padding: '15px 15px 15px 0', border: '1px solid var(--border-subtle)' }}>
                                                    <Suspense fallback={<div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.5, fontSize: '12px' }}>Loading Analytics Chart...</div>}>
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <LineChart data={chartData.data} margin={{ top: 5, right: 20, left: -20, bottom: 5 }} onClick={handleGraphClick}>
                                                                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
                                                                <XAxis 
                                                                    dataKey="fullTimestamp" 
                                                                    tickFormatter={(tickStr) => new Date(tickStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                                                    tick={{ fill: 'var(--text-muted)', fontSize: 11 }} 
                                                                    tickLine={false} 
                                                                    axisLine={false} 
                                                                />
                                                                <YAxis domain={[0, chartData.maxScale]} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickLine={false} axisLine={false} />
                                                                <Tooltip content={<ChartCustomTooltip />} cursor={{ stroke: 'var(--border-subtle)', strokeWidth: 1 }} />
                                                                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} iconType="circle" />
                                                                {chartData.lines.map((lineName, idx) => (
                                                                    <Line 
                                                                        key={lineName}
                                                                        type="monotone" 
                                                                        dataKey={lineName} 
                                                                        stroke={chartColors[idx % chartColors.length]} 
                                                                        strokeWidth={3}
                                                                        dot={{ r: 5, strokeWidth: 2, cursor: 'pointer', onClick: (e) => handleGraphClick(e) }}
                                                                        activeDot={{ r: 8, cursor: 'pointer', onClick: (e) => handleGraphClick(e) }}
                                                                        onClick={(e) => handleGraphClick(e)}
                                                                        animationDuration={1500}
                                                                    />
                                                                ))}
                                                            </LineChart>
                                                        </ResponsiveContainer>
                                                    </Suspense>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Complete assessments to generate your longitudinal trendlines.</p>
                                )}
                            </div>
                        )}

                        {/* =========================================================
                           TAB 3: MY LOGS (WITH 2 VERTICAL LEFT SUB-TABS)
                           ========================================================= */}
                        {dashMainTab === 'logs' && (
                            <div className="dashboard-subtab-layout animate-fade">
                                <div className="dashboard-subtabs-sidebar">
                                    <button 
                                        className={`dashboard-subtab-btn ${logsSubTab === 'reflective' ? 'active' : ''}`}
                                        onClick={() => setLogsSubTab('reflective')}
                                    >
                                        Reflective
                                    </button>
                                    <button 
                                        className={`dashboard-subtab-btn ${logsSubTab === 'clinical' ? 'active' : ''}`}
                                        onClick={() => setLogsSubTab('clinical')}
                                    >
                                        Clinical
                                    </button>
                                </div>

                                <div className="dashboard-subtab-content">
                                    <div className="trait-section" style={{ padding: '24px' }}>
                                        {history && history.length > 0 ? (() => {
                                            const clinicalIds = ['disc', 'fivefactor', 'dass21', 'obi16', 'uwes17', 'sds6', 'copsoq', 'archetype', 'sapphire'];
                                            const clinicalHistory = history.filter(item => clinicalIds.includes(item.toolId)).sort((a, b) => new Date(b.date) - new Date(a.date));
                                            const reflectiveHistory = history.filter(item => !clinicalIds.includes(item.toolId)).sort((a, b) => new Date(b.date) - new Date(a.date));

                                            const renderHistoryRow = (item) => (
                                                <li key={item.id} style={{ padding: '14px 0', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px' }}>
                                                    <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => { setSelectedRecord(item); setAiAnalysis(null); setGeneratedPlan(null); }}>
                                                        <strong style={{ display: 'block', fontSize: '1rem', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.tool}</strong>
                                                        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{item.summary}</span>
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                                                            {item.date && !isNaN(new Date(item.date).getTime()) 
                                                                ? new Date(item.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                                                                : 'Date Unknown'}
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <button 
                                                            onClick={(e) => { e.stopPropagation(); setSelectedRecord(item); setAiAnalysis(null); setGeneratedPlan(null); }}
                                                            style={{ background: 'transparent', border: '1px solid var(--border-subtle)', borderRadius: '6px', color: 'var(--text-main)', cursor: 'pointer', padding: '6px 10px', fontSize: '11px', fontWeight: 'bold' }}
                                                        >
                                                            View
                                                        </button>
                                                        
                                                        {historyDeleteConfirmId === item.id ? (
                                                            <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: '4px', alignItems: 'center', background: 'var(--btn-primary-bg)', padding: '2px 6px', borderRadius: '6px', border: '1px solid var(--border-subtle)' }}>
                                                                <button onClick={(e) => { e.stopPropagation(); handleDeleteHistory(item.id); }} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer' }}>Delete</button>
                                                                <button onClick={(e) => { e.stopPropagation(); setHistoryDeleteConfirmId(null); }} style={{ background: 'var(--card-bg)', color: 'var(--text-main)', border: '1px solid var(--border-subtle)', padding: '4px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer' }}>✕</button>
                                                            </div>
                                                        ) : (
                                                            <button onClick={(e) => { e.stopPropagation(); setHistoryDeleteConfirmId(item.id); }} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '6px' }} title="Delete permanently">
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                                            </button>
                                                        )}
                                                    </div>
                                                </li>
                                            );

                                            return (
                                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                    {logsSubTab === 'reflective' ? (
                                                        reflectiveHistory.length > 0 ? reflectiveHistory.map(renderHistoryRow) : <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No reflective practice logs recorded yet.</p>
                                                    ) : (
                                                        clinicalHistory.length > 0 ? clinicalHistory.map(renderHistoryRow) : <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No clinical assessment logs recorded yet.</p>
                                                    )}
                                                </ul>
                                            );
                                        })() : (
                                            <p style={{ color: 'var(--text-muted)' }}>No logs saved yet. Complete a tool to see your history here.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* =========================================================
                           TAB 4: ACCOUNT (SUBSCRIPTION & UPDATE PROFILE DATA)
                           ========================================================= */}
                        {dashMainTab === 'account' && (
                            <div className="animate-fade" style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                                
                                {/* Subscription Module */}
                                <div className="trait-section" style={{ padding: '30px' }}>
                                    <h3 style={{ margin: '0 0 20px 0', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '10px' }}>Subscription Status</h3>
                                    
                                    {isPremium ? (
                                        <div style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid #10b981', borderRadius: '8px', padding: '20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                                                    <h4 style={{ margin: 0, color: '#10b981', fontSize: '1.2rem', fontWeight: '700' }}>
                                                        {orgName && orgName.includes('Individual') ? 'Onyx Pro Active' : 'Onyx Enterprise Workspace Active'}
                                                    </h4>
                                                </div>
                                                <button 
                                                    onClick={handleRevokeLicense} 
                                                    disabled={revoking}
                                                    style={{ background: 'transparent', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}
                                                >
                                                    {revoking ? 'Revoking...' : 'Revoke / Cancel License'}
                                                </button>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                <p style={{ margin: 0, color: 'var(--text-main)', fontSize: '14px' }}>
                                                    <strong>Organization Assignment:</strong> {orgName || 'Independent Pro License'}
                                                </p>
                                                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '13px' }}>
                                                    <strong>License Tracking Key:</strong> <code style={{ background: 'var(--btn-primary-bg)', padding: '2px 6px', borderRadius: '4px' }}>{licenseId || 'ACTIVE-PRO-ACCOUNT'}</code>
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ background: 'var(--bg-main)', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                            <div>
                                                <span style={{ display: 'inline-block', background: 'var(--text-muted)', color: 'var(--bg-main)', padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Standard Free Tier</span>
                                                <h4 style={{ margin: '0 0 10px 0', fontSize: '1.2rem', color: 'var(--text-main)' }}>Unlock Deep Relational Coaching</h4>
                                                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.5 }}>
                                                    You currently have basic assessment access. Upgrade to Pro for $3/month (or $24/year) to unlock advanced AI analytics matrices and multi-vector action tracking plans.
                                                </p>
                                            </div>
                                            <button 
                                                onClick={() => setActiveView('pricing')} 
                                                style={{ background: 'var(--text-main)', color: 'var(--bg-main)', border: 'none', padding: '12px 20px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', alignSelf: 'flex-start' }}
                                            >
                                                Buy Individual Pro License
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Update Profile Data Form */}
                                <div className="trait-section" style={{ padding: '30px' }}>
                                    <h3 style={{ margin: '0 0 20px 0', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '10px' }}>Update Profile Data</h3>
                                    
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--text-muted)' }}>First Name</label>
                                            <input 
                                                type="text" 
                                                maxLength={50}
                                                value={firstName} 
                                                onChange={(e) => setFirstName(e.target.value)} 
                                                style={{ width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-subtle)', background: 'var(--bg-main)', color: 'var(--text-main)' }} 
                                            />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--text-muted)' }}>Last Name</label>
                                            <input 
                                                type="text" 
                                                maxLength={50}
                                                value={lastName} 
                                                onChange={(e) => setLastName(e.target.value)} 
                                                style={{ width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-subtle)', background: 'var(--bg-main)', color: 'var(--text-main)' }} 
                                            />
                                        </div>
                                        <div style={{ gridColumn: '1 / -1' }}>
                                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: 'var(--text-muted)' }}>Email Address <span style={{fontWeight: 'normal', fontStyle: 'italic'}}>(Locked to Google Account)</span></label>
                                            <input 
                                                type="email" 
                                                disabled 
                                                value={user.email} 
                                                style={{ width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '8px', border: '1px solid var(--border-subtle)', background: 'var(--bg-main)', color: 'var(--text-muted)', cursor: 'not-allowed', opacity: 0.7 }} 
                                            />
                                        </div>
                                    </div>
                                    <button 
                                        id="saveProfileBtn" 
                                        onClick={handleUpdateProfile} 
                                        disabled={savingProfile || !isFormChanged} 
                                        style={{ marginTop: '20px', background: isFormChanged ? 'var(--text-main)' : 'var(--btn-primary-bg)', color: isFormChanged ? 'var(--bg-main)' : 'var(--text-muted)', border: 'none', padding: '12px 25px', borderRadius: '8px', fontWeight: 'bold', cursor: isFormChanged ? 'pointer' : 'not-allowed', opacity: savingProfile ? 0.7 : 1 }}
                                    >
                                        {savingProfile ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>

                                {/* Enterprise Admin Console (if applicable) */}
                                {licenseId && orgName && !orgName.includes('Individual') && profileData?.isCompanyAdmin && (
                                    <div className="trait-section" style={{ padding: '30px', background: 'var(--card-bg)' }}>
                                        <div style={{ borderBottom: '1px solid var(--border-subtle)', paddingBottom: '12px', marginBottom: '20px' }}>
                                            <h4 style={{ margin: 0, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)' }}>
                                                💼 Corporate Administration Console
                                            </h4>
                                            <h3 style={{ margin: '4px 0 0 0', fontSize: '1.4rem', color: 'var(--text-main)' }}>{orgName} Metrics Overview</h3>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
                                            <div style={{ background: 'var(--bg-main)', padding: '16px', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
                                                <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' }}>Active Workspace Seats</span>
                                                <span style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-main)', display: 'block', marginTop: '6px' }}>
                                                    {workspaceStats ? workspaceStats.seatsUsed : '...'} / 500
                                                </span>
                                            </div>
                                            <div style={{ background: 'var(--bg-main)', padding: '16px', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
                                                <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' }}>Total Run Profiles</span>
                                                <span style={{ fontSize: '24px', fontWeight: '800', color: '#10b981', display: 'block', marginTop: '6px' }}>
                                                    {workspaceStats ? `${workspaceStats.completedAssessments} Tests` : '...'}
                                                </span>
                                            </div>
                                            <div style={{ background: 'var(--bg-main)', padding: '16px', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
                                                <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase' }}>Active Action Plans</span>
                                                <span style={{ fontSize: '24px', fontWeight: '800', color: '#3b82f6', display: 'block', marginTop: '6px' }}>
                                                    {workspaceStats ? `${workspaceStats.activeSprints} Sprints` : '...'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div style={{ textAlign: 'center', marginTop: '40px', marginBottom: '20px' }}>
                            <button 
                                onClick={() => setActiveView('tools')} 
                                style={{ 
                                    background: 'var(--btn-primary-bg)', 
                                    border: '1px solid var(--border-subtle)', 
                                    borderRadius: '20px', 
                                    padding: '12px 28px', 
                                    color: 'var(--text-main)', 
                                    cursor: 'pointer', 
                                    fontSize: '13px', 
                                    fontWeight: 'bold', 
                                    boxShadow: '0 4px 12px var(--card-shadow)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    lineHeight: 1
                                }}
                            >
                                <ArrowCircleLeftIcon size="14px" /> Close Dashboard
                            </button>
                        </div>
                    </div>
                )}

                {/* 🏢 STOREFRONT: INDIVIDUAL PRO & ENTERPRISE LICENSING */}
                {activeView === 'pricing' && (
                    <div className="animate-fade" style={{ padding: '0 20px', maxWidth: '1100px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
                        
                        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
                            <span style={{ display: 'inline-block', background: 'var(--strong-accent)', color: 'var(--text-main)', padding: '6px 14px', borderRadius: '20px', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '16px' }}>
                                Onyx Subscription & Licensing Engine
                            </span>
                            <h1 style={{ fontSize: 'clamp(2rem, 6vw, 3rem)', margin: '0 0 15px 0', color: 'var(--text-main)', letterSpacing: '-0.8px', fontWeight: '800' }}>
                                Secure Individual & Workspace Access
                            </h1>
                            <p style={{ color: 'var(--text-muted)', fontSize: '1.1rem', maxWidth: '650px', margin: '0 auto', lineHeight: 1.6 }}>
                                Unlock individual deep relational coaching or deploy Clinical-Grade Supervision across whole divisions.
                            </p>
                        </div>

                        {/* 📊 PRICING TIERS GRID */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: '30px', alignItems: 'start', marginBottom: '60px' }}>
                            
                            {/* TIER 1: STANDARD FREE ENTRY CARD */}
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-subtle)', borderRadius: '16px', padding: '40px 30px', display: 'flex', flexDirection: 'column', minHeight: '560px', boxSizing: 'border-box' }}>
                                <h3 style={{ margin: '0 0 10px 0', fontSize: '1.4rem', fontWeight: '700' }}>Standard</h3>
                                <div style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '20px', color: 'var(--text-main)' }}>$0 <span style={{ fontSize: '1rem', color: 'var(--text-muted)', fontWeight: '500' }}>/ forever</span></div>
                                <p style={{ color: 'var(--text-muted)', marginBottom: '30px', fontSize: '13.5px', lineHeight: 1.5 }}>Basic localized evaluation tools for individual practitioners.</p>
                                <ul className="trait-list pros" style={{ marginBottom: '30px', flex: 1, color: 'var(--text-main)' }}>
                                    <li>Unlimited Assessment Completions</li>
                                    <li>Executive Print PDF Records</li>
                                    <li>Mathematical Threshold Mapping</li>
                                    <li>Static Diagnostic Local Logs</li>
                                </ul>
                                <button disabled style={{ background: 'var(--btn-primary-bg)', color: 'var(--text-muted)', border: 'none', padding: '15px', borderRadius: '8px', fontWeight: 'bold', width: '100%', cursor: 'not-allowed', fontSize: '13px' }}>
                                    {user && !isPremium ? 'Active Free Plan' : 'Free Entry Tier'}
                                </button>
                            </div>

                            {/* TIER 2: INDIVIDUAL PRO PURCHASING CARD WITH PAYPAL CHECKOUT */}
                            <div style={{ background: 'var(--card-bg)', border: '2px solid var(--text-main)', borderRadius: '16px', padding: '40px 30px', display: 'flex', flexDirection: 'column', minHeight: '560px', position: 'relative', boxShadow: '0 20px 40px var(--card-shadow)', boxSizing: 'border-box' }}>
                                <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'var(--text-main)', color: 'var(--bg-main)', padding: '4px 16px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    Individual License
                                </div>
                                <h3 style={{ margin: '0 0 10px 0', fontSize: '1.4rem', fontWeight: '700' }}>Pro License</h3>
                                
                                {/* 🔄 BILLING CYCLE TOGGLE (MONTHLY VS ANNUAL) */}
                                <div style={{ display: 'flex', background: 'var(--bg-main)', borderRadius: '20px', padding: '4px', margin: '15px 0 20px 0', border: '1px solid var(--border-subtle)' }}>
                                    <button 
                                        onClick={() => setBillingCycle('monthly')} 
                                        style={{ flex: 1, padding: '8px', border: 'none', borderRadius: '16px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', background: billingCycle === 'monthly' ? 'var(--text-main)' : 'transparent', color: billingCycle === 'monthly' ? 'var(--bg-main)' : 'var(--text-muted)', transition: 'all 0.2s' }}
                                    >
                                        Pay Monthly
                                    </button>
                                    <button 
                                        onClick={() => setBillingCycle('annual')} 
                                        style={{ flex: 1, padding: '8px', border: 'none', borderRadius: '16px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', background: billingCycle === 'annual' ? '#10b981' : 'transparent', color: billingCycle === 'annual' ? '#ffffff' : 'var(--text-muted)', transition: 'all 0.2s' }}
                                    >
                                        Pay Annually (Save 33%)
                                    </button>
                                </div>

                                <div style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '10px', color: 'var(--text-main)' }}>
                                    ${billingCycle === 'annual' ? PRO_ANNUAL_MONTHLY_EQUIV : PRO_MONTHLY_PRICE} 
                                    <span style={{ fontSize: '1rem', color: 'var(--text-muted)', fontWeight: '500' }}> / month</span>
                                </div>
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '20px' }}>
                                    {billingCycle === 'annual' ? 'Billed as $24.00 / year' : 'Billed as $3.00 / month'}
                                </div>

                                <ul className="trait-list pros" style={{ marginBottom: '20px', flex: 1, color: 'var(--text-main)' }}>
                                    <li><strong>Everything in Standard, plus:</strong></li>
                                    <li>Longitudinal Recharts Progression Lines</li>
                                    <li>90-Day Holistic AI Diagnostics Engine</li>
                                    <li>Interactive Action Plan Checklists</li>
                                    <li>Automated Multi-Vector Sprints</li>
                                    <li>Relational AI Dynamics & Simulators</li>
                                </ul>

                                {/* LEGAL FINE PRINT: DISCLOSURE OF AUTO-RENEWAL & OPT-OUT */}
                                <p style={{ fontSize: '10.5px', color: 'var(--text-muted)', lineHeight: '1.4', marginBottom: '15px', fontStyle: 'italic', textAlign: 'center' }}>
                                    * Auto-renewal notice: Subscriptions renew automatically ({billingCycle === 'annual' ? '$24.00/year' : '$3.00/month'}). You can opt out or cancel auto-renewal at any time from your account settings.
                                </p>

                                {isPremium ? (
                                    <button disabled style={{ background: '#10b981', color: '#fff', border: 'none', padding: '15px', borderRadius: '8px', fontWeight: 'bold', width: '100%', cursor: 'default', fontSize: '13px' }}>
                                        ✓ Pro License Active
                                    </button>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                                        <button 
                                            onClick={handleIndividualPurchase} 
                                            disabled={processingPayment}
                                            style={{ background: '#0070ba', color: '#ffffff', border: 'none', padding: '15px', borderRadius: '8px', fontWeight: 'bold', width: '100%', cursor: 'pointer', transition: 'all 0.2s', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <span>{processingPayment ? 'Processing...' : 'Purchase via PayPal'}</span>
                                        </button>
                                        {!user && (
                                            <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', fontWeight: '500' }}>
                                                Sign into Google required
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* TIER 3: B2B CORPORATE WORKSPACE LICENSE */}
                            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-subtle)', borderRadius: '16px', padding: '40px 30px', display: 'flex', flexDirection: 'column', minHeight: '560px', boxSizing: 'border-box', boxShadow: '0 10px 30px rgba(16,185,129,0.03)' }}>
                                <span style={{ color: '#10b981', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '4px', display: 'block' }}>Corporate Suite</span>
                                <h3 style={{ margin: '0 0 10px 0', fontSize: '1.4rem', fontWeight: '700' }}>Enterprise Workspace</h3>
                                <div style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '20px', color: 'var(--text-main)' }}>$5 <span style={{ fontSize: '1rem', color: 'var(--text-muted)', fontWeight: '500' }}>/ user / mo</span></div>
                                <p style={{ color: 'var(--text-muted)', marginBottom: '30px', fontSize: '13.5px', lineHeight: 1.5 }}>Frictionless domain single sign-on mapping for whole organizations.</p>
                                <ul className="trait-list pros" style={{ marginBottom: '30px', flex: 1, color: 'var(--text-main)' }}>
                                    <li><strong>Everything in Pro, plus:</strong></li>
                                    <li>Whitelisted Domain Single Sign-On</li>
                                    <li>Aggregated Team Burnout Analytics Radar</li>
                                    <li>Anonymized Group Stress Telemetry</li>
                                    <li>Dedicated Corporate Admin Console</li>
                                </ul>
                                <a 
                                    href="#enterprise-procure"
                                    style={{ display: 'block', textDecoration: 'none', textAlign: 'center', background: 'transparent', color: '#10b981', border: '1px solid rgba(16,185,129,0.4)', padding: '15px', borderRadius: '8px', fontWeight: 'bold', width: '100%', fontSize: '13px', boxSizing: 'border-box' }}
                                >
                                    Configure Workspace
                                </a>
                            </div>

                        </div>

                        {/* ENTERPRISE PROCUREMENT HUB */}
                        <div id="enterprise-procure" className="trait-section" style={{ padding: '40px', background: 'var(--card-bg)', border: '1px solid var(--border-subtle)', borderRadius: '20px', textAlign: 'left', scrollMarginTop: '100px' }}>
                            <div style={{ borderBottom: '1px solid var(--border-subtle)', paddingBottom: '15px', marginBottom: '30px' }}>
                                <h3 style={{ margin: 0, fontSize: '1.6rem', fontWeight: '800', color: 'var(--text-main)' }}>🏢 Enterprise Pricing Calculator & Provisioning Sandbox</h3>
                                <p style={{ margin: '6px 0 0 0', fontSize: '13.5px', color: 'var(--text-muted)' }}>
                                    Determine deployment scalability and map authorized single sign-on constraints natively.
                                </p>
                            </div>

                            {procurementSuccess ? (
                                <div className="animate-fade" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '2px dashed #10b981', borderRadius: '12px', padding: '35px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '44px', marginBottom: '15px' }}>🚀</div>
                                    <h4 style={{ color: '#10b981', fontSize: '1.4rem', margin: '0 0 10px 0', fontWeight: '800' }}>Workspace Proposal Contract Staged Successfully</h4>
                                    <p style={{ margin: '0 auto 25px auto', fontSize: '14px', color: 'var(--text-main)', maxWidth: '580px', lineHeight: 1.5 }}>
                                        Onyx has generated an automated infrastructure draft block tracking <strong>{enterpriseSeats} seats</strong> for domains: {validatedDomains.map(d => <code key={d} style={{ marginLeft: '4px' }}>@{d}</code>)}.
                                    </p>
                                    <button onClick={() => { setProcurementSuccess(false); setValidatedDomains([]); }} style={{ background: 'var(--text-main)', color: 'var(--bg-main)', border: 'none', padding: '12px 24px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' }}>
                                        Reconfigure Parameters
                                    </button>
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '35px' }}>
                                    
                                    <div>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: '15px' }}>
                                            1. Select Required Employee Count: <span style={{ color: '#10b981', float: 'right', fontWeight: '900', fontSize: '15px' }}>{enterpriseSeats} Active Seats</span>
                                        </label>
                                        <input 
                                            type="range" 
                                            min={50} 
                                            max={1000} 
                                            step={10}
                                            value={enterpriseSeats}
                                            onChange={e => setEnterpriseSeats(Number(e.target.value))}
                                            style={{ width: '100%', height: '6px', background: 'var(--border-subtle)', borderRadius: '4px', cursor: 'pointer', accentColor: '#10b981', marginBottom: '25px' }}
                                        />

                                        <div style={{ background: 'var(--bg-main)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13.5px' }}>
                                                <span style={{ color: 'var(--text-muted)' }}>Volume Tier Rate:</span>
                                                <span style={{ fontWeight: '700', color: 'var(--text-main)' }}>${enterprisePricingCalculated.perSeat} / user / mo</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13.5px', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '10px' }}>
                                                <span style={{ color: 'var(--text-muted)' }}>Standard Monthly Cost:</span>
                                                <span style={{ fontWeight: '700', color: 'var(--text-main)' }}>${enterprisePricingCalculated.monthly} / mo</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '4px' }}>
                                                <div>
                                                    <span style={{ fontWeight: '800', display: 'block', fontSize: '14px', color: 'var(--text-main)' }}>Annual Value Program:</span>
                                                    <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '700' }}>✓ 15% Strategic Bulk Discount Applied</span>
                                                </div>
                                                <span style={{ fontSize: '24px', fontWeight: '900', color: '#10b981' }}>${enterprisePricingCalculated.annualized} <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'normal' }}>/ yr</span></span>
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                                                2. Define Whitelisted Access Domains:
                                            </label>
                                            <p style={{ margin: '0 0 15px 0', fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                                                Employees registering with verified emails from these domains automatically fill corporate license seats.
                                            </p>

                                            <form onSubmit={handleAddSimulatedDomain} style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
                                                <input 
                                                    type="text"
                                                    placeholder="e.g., enterprise-workspace.com"
                                                    value={simulatedDomain}
                                                    onChange={e => setSimulatedDomain(e.target.value)}
                                                    style={{ flex: 1, padding: '12px', borderRadius: '8px', border: '1px solid var(--border-subtle)', background: 'var(--bg-main)', color: 'var(--text-main)', fontSize: '13px' }}
                                                />
                                                <button type="submit" style={{ background: 'var(--text-main)', color: 'var(--bg-main)', border: 'none', padding: '0 18px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' }}>
                                                    Map Domain
                                                </button>
                                            </form>

                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', minHeight: '34px', alignItems: 'center' }}>
                                                {validatedDomains.length === 0 ? (
                                                    <span style={{ fontSize: '12.5px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No corporate single sign-on roots mapped yet.</span>
                                                ) : (
                                                    validatedDomains.map((dom) => (
                                                        <span key={dom} style={{ background: 'var(--btn-primary-bg)', border: '1px solid var(--border-subtle)', padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-main)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                            @{dom}
                                                            <button type="button" onClick={() => setValidatedDomains(validatedDomains.filter(d => d !== dom))} style={{ background: 'none', border: 'none', color: '#ef4444', padding: 0, cursor: 'pointer', fontWeight: 'bold', fontSize: '11px' }}>✕</button>
                                                        </span>
                                                    ))
                                                )}
                                            </div>
                                        </div>

                                        <button 
                                            onClick={async () => {
                                                if (validatedDomains.length === 0) return;
                                                try {
                                                    const res = await fetch('/api/enterprise/provision-draft', {
                                                        method: 'POST',
                                                        headers: { 'Content-Type': 'application/json' },
                                                        body: JSON.stringify({ seats: enterpriseSeats, domains: validatedDomains })
                                                    });
                                                    if (res.ok) setProcurementSuccess(true);
                                                } catch (err) {
                                                    alert("Failed to reach database server cluster.");
                                                }
                                            }}
                                            disabled={validatedDomains.length === 0}
                                            style={{ width: '100%', background: '#10b981', color: '#fff', border: 'none', padding: '14px', borderRadius: '8px', fontWeight: 'bold', fontSize: '14px', cursor: validatedDomains.length === 0 ? 'not-allowed' : 'pointer', opacity: validatedDomains.length === 0 ? 0.4 : 1, transition: 'all 0.2s', marginTop: '20px' }}
                                        >
                                            Deploy Workspace Infrastructure Draft
                                        </button>
                                    </div>

                                </div>
                            )}
                        </div>

                        <div style={{ textAlign: 'center', marginTop: '30px' }}>
                            <button onClick={() => setActiveView('tools')} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                                ← Return to Dashboard View
                            </button>
                        </div>

                    </div>
                )}
            </div>

            <Suspense fallback={null}>
                <TriageModal 
                    isOpen={showTriage}
                    onClose={() => setShowTriage(false)}
                />

                <SupervisionTriageModal 
                    isOpen={showSupervisionTriage}
                    onClose={() => setShowSupervisionTriage(false)}
                />
            </Suspense>

            {/* 📈 RECORD INSPECTION POPUP */}
            {selectedRecord && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
                    <div id="selected-record-card" className="printable-record animate-slide-up" style={{ background: 'var(--bg-main)', padding: '40px', borderRadius: '16px', width: '100%', maxWidth: '500px', position: 'relative', border: '1px solid var(--border-subtle)', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
                        <button className="hide-on-print" onClick={() => setSelectedRecord(null)} style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>
                        
                        <h2 style={{ margin: '0 0 5px 0', color: 'var(--text-main)' }}>{selectedRecord.tool || selectedRecord.toolName || "Assessment Record"}</h2>
                        <p style={{ margin: '0 0 30px 0', color: 'var(--text-muted)', fontSize: '14px' }}>
                            Recorded on {selectedRecord.date ? new Date(selectedRecord.date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : "Unknown Date"}
                        </p>

                        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-subtle)', borderRadius: '12px', padding: '20px', marginBottom: '30px' }}>
                            <h4 style={{ margin: '0 0 15px 0', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '10px' }}>Summary</h4>
                            <p style={{ margin: '0 0 20px 0', fontWeight: 'bold', color: 'var(--text-main)' }}>{selectedRecord.summary || "No summary provided."}</p>
                            
                            <h4 style={{ margin: '0 0 15px 0', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '10px' }}>
                                {selectedRecord.toolId === 'morethanwords' ? 'Visual Storyboard' : 
                                 selectedRecord.toolId === 'workinggenius' ? 'Working Genius Matrix' : 
                                 selectedRecord.toolId === 'target' ? 'Target Proximity Map' : 
                                 selectedRecord.toolId === 'traffic' ? 'Traffic Light Status' : 
                                 (selectedRecord.toolId === 'compass' || selectedRecord.toolId === 'compasspoints') ? 'Compass Points Analysis' : 
                                 selectedRecord.toolId === 'cec' ? 'Connect, Extend, Challenge Matrix' : 
                                 (selectedRecord.toolId === '3ys' || selectedRecord.toolId === 'threeys') ? 'Purpose Alignment Rings (The 3Ys)' : 
                                 selectedRecord.toolId === 'complexity' ? 'Systemic Complexity Analysis' : 
                                 selectedRecord.toolId === 'stepinside' ? 'Step Inside Perspective Pyramid' : 'Raw Data Matrix'}
                            </h4>
                            
                            {/* 🚀 DYNAMIC TOOL VISUALIZERS ROUTER */}
                            {selectedRecord.toolId === 'decisioncircle' ? (
                                <DecisionCircleVisualizer scores={selectedRecord.scores} />
                            ) : selectedRecord.toolId === 'connectingthreads' ? (
                                <ConnectingThreadsVisualizer scores={selectedRecord.scores} />
                            ) : selectedRecord.toolId === 'fourbatteries' ? (
                                <FourBatteriesVisualizer scores={selectedRecord.scores} />
                            ) : selectedRecord.toolId === 'bandwidthgauge' ? (
                                <BandwidthGaugeVisualizer scores={selectedRecord.scores} />
                            ) : selectedRecord.toolId === 'energyaudit' ? (
                                <EnergyAuditVisualizer scores={selectedRecord.scores} />
                            ) : selectedRecord.toolId === 'cec' ? (
                                <CecVisualizer scores={selectedRecord.scores} />
                            ) : (selectedRecord.toolId === '3ys' || selectedRecord.toolId === 'threeys') ? (
                                <ThreeYsVisualizer scores={selectedRecord.scores} />
                            ) : selectedRecord.toolId === 'complexity' ? (
                                <ComplexityVisualizer scores={selectedRecord.scores} />
                            ) : selectedRecord.toolId === 'stepinside' ? (
                                <StepInsideVisualizer scores={selectedRecord.scores} />
                            ) : (selectedRecord.toolId === 'compass' || selectedRecord.toolId === 'compasspoints') ? (
                                <CompassVisualizer scores={selectedRecord.scores} />
                            ) : selectedRecord.toolId === 'target' ? (
                                <TargetVisualizer scores={selectedRecord.scores} />
                            ) : selectedRecord.toolId === 'traffic' ? (
                                <TrafficLightVisualizer scores={selectedRecord.scores} />
                            ) : selectedRecord.toolId === 'morethanwords' && Array.isArray(selectedRecord.scores) ? (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                                    {selectedRecord.scores.map((img) => (
                                        <div key={img.slot} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--trait-bg)', padding: '10px', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                                            <div style={{ width: '100%', aspectRatio: '1', borderRadius: '6px', overflow: 'hidden', marginBottom: '10px', border: '1px solid var(--border-subtle)', background: '#141416' }}>
                                                <img 
                                                    src={img.imageUrl} 
                                                    alt={`Slot ${img.slot}`} 
                                                    crossOrigin="anonymous"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'none', opacity: 1, display: 'block' }} 
                                                />
                                            </div>
                                            <div style={{ fontSize: '10px', fontWeight: '800', color: 'var(--text-main)', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                {['1', '2', '3'][img.slot - 1] || `Slot ${img.slot}`}
                                            </div>
                                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center', marginTop: '4px' }}>
                                                By {img.author}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : selectedRecord.toolId === 'workinggenius' && selectedRecord.scores?.genius ? (
                                <div style={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'center', alignItems: 'center', gap: '8px', width: '100%', overflowX: 'auto', paddingBottom: '10px', paddingTop: '10px' }}>
                                    <div style={{ color: '#10b981', display: 'flex', gap: '4px' }}>
                                        {selectedRecord.scores.genius.map((t, i) => <ProfileGearIcon key={`g-${i}`} text={t.charAt(0).toUpperCase()} />)}
                                    </div>
                                    <div style={{ width: '2px', height: '24px', background: 'var(--border-subtle)', borderRadius: '2px' }} />
                                    <div style={{ color: '#f59e0b', display: 'flex', gap: '4px' }}>
                                        {selectedRecord.scores.comp.map((t, i) => <ProfileGearIcon key={`c-${i}`} text={t.charAt(0).toUpperCase()} />)}
                                    </div>
                                    <div style={{ width: '2px', height: '24px', background: 'var(--border-subtle)', borderRadius: '2px' }} />
                                    <div style={{ color: '#ef4444', display: 'flex', gap: '4px' }}>
                                        {selectedRecord.scores.frust.map((t, i) => <ProfileGearIcon key={`f-${i}`} text={t.charAt(0).toUpperCase()} />)}
                                    </div>
                                </div>
                            ) : selectedRecord.chartData?.labels && Array.isArray(selectedRecord.chartData.labels) ? (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    {selectedRecord.chartData.labels.map((label, i) => (
                                        <div key={label} style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                                            <strong>{label}:</strong> <span style={{ color: 'var(--text-main)' }}>{selectedRecord.chartData.values[i]}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p style={{ fontSize: '13px', color: 'var(--text-muted)', gridColumn: '1 / -1' }}>
                                    {selectedRecord.scores ? JSON.stringify(selectedRecord.scores) : 'No matrix data attached to this older record.'}
                                </p>
                            )}
                        </div>

                        {aiAnalysis && (
                            <div className="animate-fade" style={{ background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '12px', padding: '20px', marginBottom: '25px' }}>
                                <h4 style={{ margin: '0 0 10px 0', color: '#3b82f6', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>✨ AI Analysis</h4>
                                <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: 'var(--text-main)' }}>{aiAnalysis}</p>
                            </div>
                        )}

                        {generatedPlan && (
                            <div className="animate-fade" style={{ background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '12px', padding: '20px', marginBottom: '25px' }}>
                                <h4 style={{ margin: '0 0 10px 0', color: '#10b981', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '1px' }}>✅ {generatedPlan.duration}-Day Protocol</h4>
                                <p style={{ fontSize: '13px', fontStyle: 'italic', marginBottom: '15px' }}>{generatedPlan.rationale}</p>
                                <ul style={{ paddingLeft: '20px', margin: 0, fontSize: '14px', lineHeight: 1.6 }}>
                                    {generatedPlan.days.map(d => (
                                        <li key={d.day} style={{ marginBottom: '8px' }}>
                                            <strong>Day {d.day}:</strong> {d.task}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="hide-on-print" style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                            <button 
                                className="age-btn" 
                                onClick={() => handleShareRecord(selectedRecord)} 
                                style={{ 
                                    width: '100%', 
                                    background: '#31b1c6', 
                                    color: '#ffffff', 
                                    border: 'none', 
                                    padding: '14px', 
                                    cursor: 'pointer', 
                                    fontWeight: 'bold', 
                                    borderRadius: '8px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    gap: '8px' 
                                }}
                            >
                                <ShareIcon size="16px" /> Share / Email Record
                            </button>

                            <button 
                                className="age-btn" 
                                onClick={() => window.print()} 
                                style={{ width: '100%', background: 'var(--text-main)', color: 'var(--bg-main)', border: 'none', padding: '14px', cursor: 'pointer', fontWeight: 'bold', borderRadius: '8px' }}
                            >
                                Print Record PDF
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}